import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Chip, Fade, Typography, IconButton,
    TextField, Stepper, Step, StepLabel, StepContent,
    Paper, Card, CardContent, CircularProgress,
    Alert, FormControl, InputLabel, Select, MenuItem,
    Grid, Divider, Collapse, List, ListItem, ListItemIcon, ListItemButton,
    ListItemText, Checkbox, Tabs, Tab, Switch, InputAdornment
} from '@mui/material';
import {
    Domain, Security, Dns, CloudQueue, CheckCircle,
    Close as CloseIcon, Search, ShoppingCart, ArrowBack,
    HowToReg, Timer, ExpandMore, ExpandLess,
    Storage, Api, Language, WebAsset,
    Settings, Add, Delete, Edit, Save,
    AttachMoney, CreditCard, DataObject, Wifi, Functions,
    ContentCopy, Visibility, VisibilityOff, FolderOpen, InsertDriveFile,
    AdminPanelSettings
} from "@mui/icons-material";
import {isMobile, isInIframe, getQueryParamValue} from "../App/utils";
import { getCountries, getBaseUrl } from "../utils";
import {
    registerDomain,
    resetDomainParking,
    removeDomainParking,
    parkDomainStep1,
    parkDomainStep2,
    parkDomainStatus,
    parkDomainStep3,
    parkDomainStep4,
    checkCloudFrontStatus,
    getDomainParkingState,
    updateDomainPublicSettings,
    getDomainPublicSettings,
    checkDomainAvailability,
    registerNewDomain,
    checkDomainRegistrationStatus,
    updateAgencyPrivateData,
    getAgencyPrivateData,
    getDomainSubusers,
    setSubuserBillingActive,
    registerExternalDomain,
    verifyExternalDomainNS,
    // Elastic services
    getElasticStorage,
    enableElasticStorage,
    deleteElasticStorage,
    setElasticStoragePublic,
    setSiteSPA,
    listStorageObjects,
    deleteStorageObject,
    getElasticPostgres,
    enableElasticPostgres,
    deleteElasticPostgres,
    getElasticPostgresConnection,
    getElasticPulse,
    enableElasticPulse,
    disableElasticPulse,
    listElasticFunctions,
    getElasticFunction,
    deleteElasticFunction,
    getElasticFunctionLogs,
    getKBShares,
    shareKBWith,
    unshareKBWith,
    renameKB
} from "../API/KB/KBAPI.js";
import { parkedDomain } from "../App/utils";
import { encrypt, getKBKey } from "../App/cryptoUtils";
import { AlertContext } from "../App/App";
import { useLocation, useNavigate } from 'react-router-dom';
import { goHome } from '../utils';
import SubscriptionPlansManager from './SubscriptionPlansManager';
import LegalDocumentsEditor from './LegalDocumentsEditor';
import { KBNamePlural } from "../names";
import { CloudUpload, Image as ImageIcon, Info, RefreshOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { uploadFileAPI } from '../API/KB/Files';

// Helper function to generate random test token
const generateRandomToken = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let token = '';
    for (let i = 0; i < 16; i++) {
        token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
};

// Helper function to determine status message type
const getStatusType = (status) => {
    if (!status) return 'error';

    const successKeywords = ['ready', 'live', 'Successfully', 'registered successfully', 'available for registration'];
    const infoKeywords = [
        'Processing', 'InProgress', 'Deploying', 'Status:', 'Checking', 'Waiting',
        'Initializing', 'Configuring', 'Validating', 'Security', 'Infrastructure',
        'verification', 'Registering', 'Initiating', 'not available', 'exceeds maximum',
        'not supported', 'UNSUPPORTED_TLD', 'PRICE_TOO_HIGH'
    ];

    if (successKeywords.some(keyword => status.includes(keyword))) {
        return 'success';
    }
    if (infoKeywords.some(keyword => status.includes(keyword))) {
        return 'info';
    }
    return 'error';
};

const servicesList = [
    { domain: 'auth.vpc1.us', desc: 'Authentication Service' },
    { domain: 'chat.vpc1.us', desc: 'Chat API Service' },
    { domain: 'kb.vpc1.us', desc: 'Knowledge Base API' },
    { domain: 'nodejs.vpc1.us', desc: 'Node.js Code Executor' },
    { domain: 'items.vpc1.us', desc: 'NoSQL Items Database' },
    { domain: 'web.vpc1.us', desc: 'Web Tools Suite' },
    { domain: 'openai.vpc1.us', desc: 'OpenAI AI Gateway' },
    { domain: 'api.vpc1.us', desc: 'Agent API Service' },
    { domain: 'doctools.vpc1.us', desc: 'Document Tools Suite' },
    { domain: 'webtools.vpc1.us', desc: 'Web Tools Suite' },
    { domain: 'ledger.vpc1.us', desc: 'Transaction Ledger' },
    { domain: 'file.vpc1.us', desc: 'File Storage CDN' },
    { domain: 'files.vpc1.us', desc: 'File Upload Service' },
    { domain: 'pipe.vpc1.us', desc: 'Open Source Models Network' },
    { domain: 'app.vpc1.us', desc: 'Public App Control Service' }
];

const DEFAULT_SUBSCRIPTION_PLANS = [
    {
        id: 'starter',
        name: 'Starter',
        stripe_price_id: '',
        price: 39,
        currency: 'EUR',
        symbol: '€',
        credits: 1000,
        agents: 1,
        description: 'Perfect for getting started',
        targetAudience: 'Individual entrepreneurs and small businesses',
        premiumSupport: false
    },
    {
        id: 'pro',
        name: 'Pro',
        stripe_price_id: '',
        price: 199,
        currency: 'EUR',
        symbol: '€',
        credits: 6000,
        agents: 5,
        description: 'For growing businesses',
        targetAudience: 'Professional teams and growing companies',
        premiumSupport: true
    },
    {
        id: 'business',
        name: 'Business',
        stripe_price_id: '',
        price: 499,
        currency: 'EUR',
        symbol: '€',
        credits: 18000,
        agents: 20,
        description: 'Scale your business operations',
        targetAudience: 'Established companies and scaling startups',
        premiumSupport: true
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        stripe_price_id: '',
        price: 999,
        currency: 'EUR',
        symbol: '€',
        credits: 40000,
        agents: 50,
        description: 'Complete enterprise solution',
        targetAudience: 'Large organizations and enterprise teams',
        premiumSupport: true
    }
]

export const PlatformSetupWidget = ({ kbId, KBData }) => {
    const [dialogOpen, setDialogOpen] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const { setSystemAlert } = useContext(AlertContext);
    const location = useLocation();
    const navigate = useNavigate();

    // Check for reset_subscriptions parameter
    const shouldResetSubscriptions = new URLSearchParams(location.search).get('reset_subscriptions') === '1';

    // Project name (editable when no domain)
    const [projectName, setProjectName] = useState(KBData?.kbTitle || '');
    const [editingName, setEditingName] = useState(false);

    const handleSaveProjectName = async () => {
        setEditingName(false);
        if (!projectName || projectName === KBData?.kbTitle) return;
        try {
            const key = getKBKey(kbId);
            const encryptedTitle = encrypt(projectName, key);
            await renameKB(encryptedTitle, projectName);
        } catch (error) {
            console.error('Error renaming project:', error);
        }
    };

    // All state management moved here
    const [parkedDomain, setParkedDomain] = useState('');
    const [domainParkingStatus, setDomainParkingStatusRaw] = useState(null);

    // Smart status setter with backward compatibility
    const setDomainParkingStatus = (status) => {
        if (!status) {
            setDomainParkingStatusRaw(null);
            return;
        }

        if (typeof status === 'string') {
            // Legacy support - try to guess type from keywords for backward compatibility
            const type = getStatusType(status);
            setDomainParkingStatusRaw({ message: status, type });
        } else {
            // New format with explicit type
            setDomainParkingStatusRaw(status);
        }
    };
    const [parkingStep, setParkingStep] = useState(0);
    const [dnsRecords, setDnsRecords] = useState(null);
    const [certificateArn, setCertificateArn] = useState(null);
    const [cloudfrontDomain, setCloudfrontDomain] = useState(null);
    const [distributionId, setDistributionId] = useState(null);
    const [isPolling, setIsPolling] = useState(false);
    const [domainType, setDomainType] = useState(null); // 'new' or 'external'
    const [externalDomainNameservers, setExternalDomainNameservers] = useState(null);
    const [nsVerificationAttempts, setNsVerificationAttempts] = useState(0);
    const [domainPublicSettings, setDomainPublicSettings] = useState({
        COMPANY_NAME: '',
        GOOGLE_CLIENT_ID: '',
        SUBSCRIPTION_PLANS: DEFAULT_SUBSCRIPTION_PLANS,
        CREDIT_PRICING: {
            creditsPer10Units: 500,
            currency: 'EUR',
            symbol: '€',
            disabled: true
        },
        REGISTRATION_BONUS: {
            credits: 500
        },
        ADVANCED_SETTINGS: {
            TRANSACTIONS: true
        },
        WELCOME_EMAIL_TEMPLATE: {
            subject: '',
            htmlBody: '',
            textBody: ''
        }
    });

    const [agencyPrivateData, setAgencyPrivateData] = useState({
        STRIPE_SECRET_KEY: '',
        STRIPE_WEBHOOK_SECRET: '',
        STRIPE_SECRET_KEY_TEST: '',
        STRIPE_WEBHOOK_SECRET_TEST: '',
        stripeTestModeToken: ''
    });

    const [savingPrivateData, setSavingPrivateData] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const [domainAvailability, setDomainAvailability] = useState(null);
    const [showRegistrationForm, setShowRegistrationForm] = useState(false);
    const [domainContactInfo, setDomainContactInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        organizationName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        countryCode: 'US',
        zipCode: '',
        contactType: 'PERSON'
    });
    const [registeringDomain, setRegisteringDomain] = useState(false);
    const [pollingRegistrationStatus, setPollingRegistrationStatus] = useState(false);
    const [availableCountries, setAvailableCountries] = useState([]);
    const [expandedMicroservices, setExpandedMicroservices] = useState(false);
    const [expandedStorage, setExpandedStorage] = useState(false);
    const [expandedAdvancedSettings, setExpandedAdvancedSettings] = useState(false);
    const [domainPrice, setDomainPrice] = useState(null);
    const SETTINGS_SECTIONS = ['general', 'pricing', 'stripe', 'users', 'admins', 'storage', 'database', 'pulse', 'functions'];

    const [activeSettingsTab, setActiveSettingsTabState] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const section = params.get('section');
        const index = SETTINGS_SECTIONS.indexOf(section);
        return index >= 0 ? index : 0;
    });

    const setActiveSettingsTab = (tabIndex) => {
        setActiveSettingsTabState(tabIndex);
        const params = new URLSearchParams(window.location.search);
        params.set('section', SETTINGS_SECTIONS[tabIndex] || 'general');
        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    };
    const [expandedCloudSetup, setExpandedCloudSetup] = useState(false);
    const [uploadingLogoIcon, setUploadingLogoIcon] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);
    const [logoIconUploadProgress, setLogoIconUploadProgress] = useState(0);
    const [logoUploadProgress, setLogoUploadProgress] = useState(0);
    const [faviconUploadProgress, setFaviconUploadProgress] = useState(0);
    const [logoIconExists, setLogoIconExists] = useState(false);
    const [logoExists, setLogoExists] = useState(false);
    const [faviconExists, setFaviconExists] = useState(false);
    const [domainSubusers, setDomainSubusers] = useState([]);
    const [loadingSubusers, setLoadingSubusers] = useState(false);

    // Elastic Storage state
    const [elasticStorage, setElasticStorage] = useState(null);
    const [storageLoading, setStorageLoading] = useState(false);
    const [storageObjects, setStorageObjects] = useState([]);
    const [storageObjectsLoading, setStorageObjectsLoading] = useState(false);
    const [deletingStorageObject, setDeletingStorageObject] = useState(null);
    const [spaEditing, setSpaEditing] = useState(false);
    const [spaPath, setSpaPath] = useState('');
    const [spaSaving, setSpaSaving] = useState(false);

    // Elastic Postgres state
    const [elasticPostgres, setElasticPostgres] = useState(null);
    const [postgresLoading, setPostgresLoading] = useState(false);
    const [postgresConnection, setPostgresConnection] = useState(null);
    const [showConnectionString, setShowConnectionString] = useState(false);
    const [showStripeField, setShowStripeField] = useState({});
    const [deletePostgresDialogOpen, setDeletePostgresDialogOpen] = useState(false);

    // Elastic Pulse state
    const [elasticPulse, setElasticPulse] = useState(null);
    const [pulseLoading, setPulseLoading] = useState(false);

    // Elastic Functions state
    const [elasticFunctions, setElasticFunctions] = useState([]);
    const [functionsLoading, setFunctionsLoading] = useState(false);
    const [selectedFunction, setSelectedFunction] = useState(null);
    const [functionLogs, setFunctionLogs] = useState([]);
    const [functionLogsLoading, setFunctionLogsLoading] = useState(false);
    const [deleteFunctionDialogOpen, setDeleteFunctionDialogOpen] = useState(false);
    const [functionToDelete, setFunctionToDelete] = useState(null);

    // Admins state
    const [adminEmail, setAdminEmail] = useState('');
    const [kbShares, setKbShares] = useState([]);
    const [collaboratorsLoading, setCollaboratorsLoading] = useState(false);
    const [collaboratorsError, setCollaboratorsError] = useState('');

    // Auto-load domain subusers when My Users tab is opened
    useEffect(() => {
        const loadSubusers = async () => {
            if (activeSettingsTab === 3 && domainSubusers.length === 0 && !loadingSubusers) {
                setLoadingSubusers(true);
                try {
                    const users = await getDomainSubusers();
                    setDomainSubusers(users || []);
                } catch (error) {
                    setSystemAlert({
                        type: 'error',
                        message: error.message || 'Failed to load domain users'
                    });
                } finally {
                    setLoadingSubusers(false);
                }
            }
        };
        loadSubusers();
    }, [activeSettingsTab]);

    // Auto-load elastic storage when Storage tab is opened
    useEffect(() => {
        const loadStorage = async () => {
            if (activeSettingsTab === 5 && !elasticStorage && !storageLoading) {
                setStorageLoading(true);
                try {
                    const storage = await getElasticStorage();
                    setElasticStorage(storage || { enabled: false });
                    if (storage?.enabled) {
                        setStorageObjectsLoading(true);
                        const objects = await listStorageObjects();
                        setStorageObjects(objects?.objects || []);
                        setStorageObjectsLoading(false);
                    }
                } catch (error) {
                    setSystemAlert({
                        type: 'error',
                        message: error.message || 'Failed to load storage info'
                    });
                } finally {
                    setStorageLoading(false);
                }
            }
        };
        loadStorage();
    }, [activeSettingsTab]);

    // Auto-load elastic postgres when Database tab is opened
    useEffect(() => {
        const loadPostgres = async () => {
            if (activeSettingsTab === 6 && !elasticPostgres && !postgresLoading) {
                setPostgresLoading(true);
                try {
                    const postgres = await getElasticPostgres();
                    setElasticPostgres(postgres || { enabled: false });
                } catch (error) {
                    setSystemAlert({
                        type: 'error',
                        message: error.message || 'Failed to load database info'
                    });
                } finally {
                    setPostgresLoading(false);
                }
            }
        };
        loadPostgres();
    }, [activeSettingsTab]);

    // Auto-load elastic pulse when Pulse tab is opened
    useEffect(() => {
        const loadPulse = async () => {
            if (activeSettingsTab === 7 && !elasticPulse && !pulseLoading) {
                setPulseLoading(true);
                try {
                    const pulse = await getElasticPulse();
                    setElasticPulse(pulse || { enabled: false });
                } catch (error) {
                    setSystemAlert({
                        type: 'error',
                        message: error.message || 'Failed to load pulse info'
                    });
                } finally {
                    setPulseLoading(false);
                }
            }
        };
        loadPulse();
    }, [activeSettingsTab]);

    // Auto-load elastic functions when Functions tab is opened
    useEffect(() => {
        const loadFunctions = async () => {
            if (activeSettingsTab === 8 && elasticFunctions.length === 0 && !functionsLoading) {
                setFunctionsLoading(true);
                try {
                    const functions = await listElasticFunctions();
                    setElasticFunctions(functions?.functions || []);
                } catch (error) {
                    setSystemAlert({
                        type: 'error',
                        message: error.message || 'Failed to load functions'
                    });
                } finally {
                    setFunctionsLoading(false);
                }
            }
        };
        loadFunctions();
    }, [activeSettingsTab]);

    // Auto-load admins when Admins tab is opened
    const fetchCollaborators = async () => {
        setCollaboratorsLoading(true);
        setCollaboratorsError('');
        try {
            const res = await getKBShares();
            setKbShares(res?.sharedWith || []);
        } catch (error) {
            setCollaboratorsError(error.message || 'Failed to load admins');
        } finally {
            setCollaboratorsLoading(false);
        }
    };

    useEffect(() => {
        if (activeSettingsTab === 4 && kbShares.length === 0 && !collaboratorsLoading) {
            fetchCollaborators();
        }
    }, [activeSettingsTab]);

    const handleAddAdmin = async () => {
        if (!adminEmail.trim()) return;
        setCollaboratorsError('');
        try {
            await shareKBWith(adminEmail.trim());
            setAdminEmail('');
            await fetchCollaborators();
        } catch (error) {
            setCollaboratorsError(error.message || 'Failed to add admin');
        }
    };

    const handleRemoveAdmin = async (email) => {
        try {
            await unshareKBWith(email);
            setKbShares(kbShares.filter(s => s !== email));
        } catch (error) {
            setCollaboratorsError(error.message || 'Failed to remove admin');
        }
    };

    // Load countries list
    useEffect(() => {
        try {
            const countries = getCountries();
            const regionNames = new Intl.DisplayNames(['en-US'], { type: 'region' });
            const countryList = countries.map(code => ({
                code,
                name: regionNames.of(code)
            })).sort((a, b) => a.name.localeCompare(b.name));
            setAvailableCountries(countryList);
        } catch (error) {
            console.error('Error loading countries:', error);
            // Fallback to a basic list
            setAvailableCountries([
                { code: 'US', name: 'United States' },
                { code: 'CA', name: 'Canada' },
                { code: 'GB', name: 'United Kingdom' },
                { code: 'DE', name: 'Germany' },
                { code: 'FR', name: 'France' }
            ]);
        }
    }, []);

    // We'll let the images themselves determine if they exist via onLoad/onError
    // This avoids CORS issues with fetch
    const checkLogosExist = (domain) => {
        // Just set the domain, let img tags handle existence check
        if (domain) {
            // Trigger re-render with new timestamps
            setLogoIconExists(true); // Optimistically set to true
            setLogoExists(true); // Optimistically set to true
            setFaviconExists(true); // Optimistically set to true
        }
    };

    // Check logos when domain is available
    useEffect(() => {
        if (parkedDomain && parkingStep === 6) {
            checkLogosExist(parkedDomain);
        }
    }, [parkedDomain, parkingStep]);

    // Auto-populate welcome email template when company name changes
    useEffect(() => {
        if (domainPublicSettings.COMPANY_NAME &&
            (!domainPublicSettings.WELCOME_EMAIL_TEMPLATE?.subject &&
             !domainPublicSettings.WELCOME_EMAIL_TEMPLATE?.htmlBody &&
             !domainPublicSettings.WELCOME_EMAIL_TEMPLATE?.textBody)) {

            const companyName = domainPublicSettings.COMPANY_NAME;
            const defaultTemplate = {
                subject: `Welcome to ${companyName}`,
                htmlBody: `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Welcome to {companyName}</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background-color: #f8f9fa; padding: 20px; text-align: center; border-radius: 5px; }
        .content { padding: 20px 0; }
        .footer { text-align: center; padding-top: 20px; color: #666; font-size: 14px; }
        a { color: #007bff; text-decoration: none; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Welcome to {companyName}</h1>
        </div>
        <div class="content">
            <p>Hi there,</p>
            <p>Thank you for joining <strong>{companyName}</strong>! We're excited to have you as part of our community.</p>
            <p>You can get started by visiting our platform:</p>
            <p style="text-align: center;">
                <a href="https://{domain}" style="background-color: #007bff; color: white; padding: 10px 20px; border-radius: 5px; display: inline-block;">Visit {companyName}</a>
            </p>
            <p>If you have any questions, feel free to reach out to our support team.</p>
            <p>Best regards,<br>The {companyName} Team</p>
        </div>
        <div class="footer">
            <p>© ${new Date().getFullYear()} {companyName}. All rights reserved.</p>
        </div>
    </div>
</body>
</html>`,
                textBody: `Welcome to {companyName}

Hi there,

Thank you for joining {companyName}! We're excited to have you as part of our community.

You can get started by visiting our platform at: https://{domain}

If you have any questions, feel free to reach out to our support team.

Best regards,
The {companyName} Team

© ${new Date().getFullYear()} {companyName}. All rights reserved.`
            };

            setDomainPublicSettings(prev => ({
                ...prev,
                WELCOME_EMAIL_TEMPLATE: defaultTemplate
            }));
        }
    }, [domainPublicSettings.COMPANY_NAME]);

    // Load existing domain parking state on component mount
    useEffect(() => {
        if (isInIframe() && !getQueryParamValue('allowIframe')) return;
        if (!kbId) return;

        const loadDomainParkingState = async () => {
            try {
                const result = await getDomainParkingState();

                if (result && result.domainParkingState) {
                    const state = result.domainParkingState;
                    setParkedDomain(state.domain || '');

                    // Check if this is an external domain awaiting NS verification
                    if (state.isExternalDomain && state.step === 'awaiting-ns-propagation') {
                        setDomainType('external');
                        setExternalDomainNameservers(state.nameservers);
                        setParkingStep(0); // Stay at step 0 to show NS instructions
                        setDomainParkingStatus({
                            message: 'External domain awaiting NS record verification',
                            type: 'info'
                        });
                        // Start automatic polling for NS verification
                        // Store interval ID in ref to clean up later
                        let nsPollingInterval;

                        if (!isPolling) {
                            setIsPolling(true);
                            nsPollingInterval = setInterval(async () => {
                                try {
                                    const result = await verifyExternalDomainNS();
                                    if (result && result.verified) {
                                        clearInterval(nsPollingInterval);
                                        setIsPolling(false);
                                        setDomainParkingStatus({
                                            message: 'NS records verified! Proceeding...',
                                            type: 'success'
                                        });
                                        setParkingStep(1);
                                        setExternalDomainNameservers(null);
                                    }
                                } catch (error) {
                                    console.error('NS polling error:', error);
                                }
                            }, 30000);
                        }
                    }

                    // Load domain public settings if domain exists
                    if (state.domain) {
                        try {
                            const settings = await getDomainPublicSettings(state.domain);
                            if (settings) {
                                setDomainPublicSettings(prev => ({
                                    ...prev,
                                    COMPANY_NAME: settings.COMPANY_NAME || '',
                                    GOOGLE_CLIENT_ID: settings.GOOGLE_CLIENT_ID || '',
                                    SUBSCRIPTION_PLANS: settings.SUBSCRIPTION_PLANS || prev.SUBSCRIPTION_PLANS,
                                    CREDIT_PRICING: settings.CREDIT_PRICING || prev.CREDIT_PRICING,
                                    REGISTRATION_BONUS: settings.REGISTRATION_BONUS || prev.REGISTRATION_BONUS,
                                    ADVANCED_SETTINGS: settings.ADVANCED_SETTINGS || prev.ADVANCED_SETTINGS,
                                    PRIVACY_POLICY: settings.PRIVACY_POLICY || prev.PRIVACY_POLICY,
                                    TERMS_OF_USE: settings.TERMS_OF_USE || prev.TERMS_OF_USE,
                                    WELCOME_EMAIL_TEMPLATE: settings.WELCOME_EMAIL_TEMPLATE || prev.WELCOME_EMAIL_TEMPLATE
                                }));

                                // Check if logos exist on the domain
                                if (state.domain) {
                                    checkLogosExist(state.domain);
                                }

                                // Load agency private data
                                loadAgencyPrivateData();
                            }
                        } catch (error) {
                            console.error('Error loading domain public settings:', error);
                        }
                    }

                    // Set UI step based on backend state
                    switch (state.step) {
                        case 'registration-pending':
                            setParkingStep(0);
                            setDomainParkingStatus({ message: `Domain registration in progress for ${state.domain}...`, type: 'info' });
                            setPollingRegistrationStatus(true);
                            startDomainRegistrationPolling();
                            break;
                        case 'registration-failed':
                            setParkingStep(0);
                            setDomainParkingStatus({ message: `Domain registration failed: ${state.failureReason || 'Unknown error'}`, type: 'error' });
                            break;
                        case 'registered':
                            setParkingStep(1);
                            setDomainParkingStatus({ message: `Domain ${state.domain} is registered and ready for SSL process`, type: 'success' });
                            break;
                        case 'certificate-requested':
                            setParkingStep(2);
                            setCertificateArn(state.certificateArn);
                            setDnsRecords(state.dnsRecords || []);
                            setDomainParkingStatus({ message: 'Security protocols initialized. Verifying domain ownership...', type: 'info' });
                            break;
                        case 'dns-records-added':
                            setParkingStep(3);
                            setCertificateArn(state.certificateArn);
                            setDnsRecords(state.dnsRecords || []);
                            setDomainParkingStatus({ message: 'Infrastructure configured. Validating security certificates...', type: 'info' });
                            // Start polling if not already validated
                            if (state.certificateArn) {
                                startCertificatePolling();
                            }
                            break;
                        case 'certificate-validated':
                            setParkingStep(3);
                            setCertificateArn(state.certificateArn);
                            setDomainParkingStatus({ message: 'Certificate validated. Ready to create AI Cloud infrastructure.', type: 'success' });
                            // Auto-proceed to step 3
                            setTimeout(() => handleParkDomainStep3(), 1000);
                            break;
                        case 'apps-cloudfront-created':
                            setParkingStep(4);
                            setCertificateArn(state.certificateArn);
                            setDistributionId(state.appsDistributionId);
                            setCloudfrontDomain(state.appsDistributionDomain);
                            setDomainParkingStatus('Apps infrastructure created. Creating public portal...');
                            // Auto-proceed to step 4
                            setTimeout(() => handleParkDomainStep4(), 1000);
                            break;
                        case 'apps-deployed':
                            setParkingStep(4);
                            setCertificateArn(state.certificateArn);
                            setDistributionId(state.appsDistributionId);
                            setCloudfrontDomain(state.appsDistributionDomain);
                            setDomainParkingStatus('Apps deployed. Creating public portal...');
                            setTimeout(() => handleParkDomainStep4(), 1000);
                            break;
                        case 'cloudfront-created':
                            setParkingStep(5);
                            setCertificateArn(state.certificateArn);
                            setDistributionId(state.filesDistributionId);
                            setCloudfrontDomain(state.filesDistributionDomain);
                            setDomainParkingStatus({ message: 'Public portal created. Waiting for deployment...', type: 'info' });
                            // Start CloudFront polling
                            if (state.filesDistributionId) {
                                startCloudFrontPolling();
                            }
                            break;
                        case 'completed':
                            setParkingStep(6);  // Set to 6 for completed domains
                            setCertificateArn(state.certificateArn);
                            setDistributionId(state.distributionId);
                            setCloudfrontDomain(state.distributionDomain);
                            // setDomainParkingStatus({ message: `AI Cloud fully deployed and ready! Your domain ${state.domain} is now live!`, type: 'success' });
                            break;
                        default:
                            setParkingStep(0);
                            break;
                    }
                }
            } catch (error) {
                console.error('Error loading domain parking state:', error);
                // Don't show error to user, just start fresh
            } finally {
                setInitialLoading(false);
            }
        };

        loadDomainParkingState();
    }, [kbId]);


    // Poll for domain registration status
    const startDomainRegistrationPolling = async () => {
        setPollingRegistrationStatus(true);

        let pollInterval;
        let timeoutId;

        // Check immediately first
        const checkStatus = async () => {
            try {
                const status = await checkDomainRegistrationStatus();

                if (status.isComplete) {
                    if (pollInterval) clearInterval(pollInterval);
                    if (timeoutId) clearTimeout(timeoutId);
                    setPollingRegistrationStatus(false);
                    setParkingStep(1);
                    setDomainParkingStatus({ message: `Domain ${status.domain} registered successfully! Ready for SSL process.`, type: 'success' });
                    return true; // Stop polling
                } else if (status.isFailed) {
                    if (pollInterval) clearInterval(pollInterval);
                    if (timeoutId) clearTimeout(timeoutId);
                    setPollingRegistrationStatus(false);
                    setDomainParkingStatus({ message: `Domain registration failed: ${status.message}`, type: 'error' });
                    return true; // Stop polling
                } else {
                    setDomainParkingStatus({ message: `Domain registration in progress... Status: ${status.status}`, type: 'info' });
                    return false; // Continue polling
                }
            } catch (error) {
                console.error('Registration polling error:', error);
                // Don't stop polling on error, might be temporary
                return false;
            }
        };

        // Check immediately
        const shouldStop = await checkStatus();
        if (shouldStop) return;

        // Then continue polling every 30 seconds
        pollInterval = setInterval(checkStatus, 30000);

        // Stop polling after 45 minutes
        timeoutId = setTimeout(() => {
            if (pollInterval) clearInterval(pollInterval);
            setPollingRegistrationStatus(false);
            setDomainParkingStatus({ message: 'Domain registration timeout - please check AWS console for status', type: 'error' });
        }, 2700000);
    };

    // Check domain availability
    const handleCheckAvailability = async () => {
        try {
            setCheckingAvailability(true);
            setDomainParkingStatus({ message: 'Checking domain availability...', type: 'info' });

            const availability = await checkDomainAvailability(parkedDomain);
            setDomainAvailability(availability);

            if (availability.available && availability.pricing) {
                setDomainParkingStatus({ message: `Domain ${parkedDomain} is available for registration!`, type: 'success' });
                setDomainPrice(availability.pricing);
                setShowRegistrationForm(true);
            } else if (availability.available && !availability.pricing) {
                setDomainParkingStatus({ message: `Domain ${parkedDomain} is available but pricing information is not available. Cannot proceed with registration.`, type: 'error' });
                setShowRegistrationForm(false);
            } else if (availability.status === 'PRICE_TOO_HIGH') {
                setDomainParkingStatus({ message: availability.error || `Domain price exceeds maximum allowed`, type: 'info' });
            } else if (availability.status === 'UNSUPPORTED_TLD') {
                setDomainParkingStatus({ message: availability.error || `This domain extension is not supported`, type: 'info' });
            } else {
                setDomainParkingStatus({ message: `Domain ${parkedDomain} is not available. Status: ${availability.status}`, type: 'info' });
            }
        } catch (error) {
            console.error('Error checking domain availability:', error);
            setDomainParkingStatus({ message: `Error checking availability: ${error.message}`, type: 'error' });
        } finally {
            setCheckingAvailability(false);
        }
    };

    // Register new domain with contact info
    const handleRegisterNewDomain = async () => {
        try {
            // Prevent registration if price is not available
            if (!domainPrice || !domainPrice.priceInCredits) {
                setDomainParkingStatus({ message: 'Cannot proceed with registration: pricing information is not available', type: 'error' });
                return;
            }

            setRegisteringDomain(true);
            setDomainParkingStatus({ message: 'Initiating domain registration...', type: 'info' });

            const domainInfo = {
                domain: parkedDomain,
                contactInfo: domainContactInfo,
                autoRenew: true,
                durationInYears: 1,
                priceInCents: Math.round(domainPrice.priceInCredits / 1000)  // Convert credits to cents
            };

            const result = await registerNewDomain(domainInfo);

            if (result.success) {
                setDomainParkingStatus({ message: result.message, type: 'success' });
                setShowRegistrationForm(false);
                startDomainRegistrationPolling();
            } else {
                setDomainParkingStatus({ message: `Registration failed: ${result.message}`, type: 'error' });
            }
        } catch (error) {
            console.error('Error registering domain:', error);
            setDomainParkingStatus({ message: `Error: ${error.message}`, type: 'error' });
        } finally {
            setRegisteringDomain(false);
        }
    };

    // Handler functions
    const handleRegisterDomain = async () => {
        try {
            // If this is an external domain, use the external domain flow
            if (domainType === 'external') {
                setDomainParkingStatus({ message: 'Creating hosted zone for external domain...', type: 'info' });
                const result = await registerDomain(parkedDomain, true); // isExternalDomain = true

                if (result && result.success) {
                    setExternalDomainNameservers(result.nameservers);
                    setDomainParkingStatus({
                        message: 'Hosted zone created. Please update your NS records at your domain registrar.',
                        type: 'success'
                    });
                    // Start NS verification polling - inline implementation to avoid hoisting issue
                    setIsPolling(true);
                    const pollInterval = setInterval(async () => {
                        try {
                            const result = await verifyExternalDomainNS();
                            if (result && result.verified) {
                                clearInterval(pollInterval);
                                setIsPolling(false);
                                setDomainParkingStatus({
                                    message: 'NS records verified! Domain ready for parking.',
                                    type: 'success'
                                });
                                setParkingStep(1);
                                setExternalDomainNameservers(null);
                            }
                        } catch (error) {
                            console.error('NS polling error:', error);
                        }
                    }, 30000);
                } else {
                    setDomainParkingStatus({
                        message: result?.message || 'Failed to create hosted zone',
                        type: 'error'
                    });
                }
                return;
            }

            // Original flow for new domain registration
            setDomainParkingStatus({ message: 'Registering domain for AI Cloud...', type: 'info' });

            const result = await registerDomain(parkedDomain, false);

            if (result && result.success) {
                setParkingStep(1);
                setDomainParkingStatus({ message: `Domain ${parkedDomain} registered successfully!`, type: 'success' });
            } else if (result && result.availableForRegistration) {
                // Domain needs to be registered with AWS Route53 Domains
                // First check the price by calling checkDomainAvailability
                setDomainParkingStatus({ message: 'Checking domain availability and pricing...', type: 'info' });

                try {
                    const availability = await checkDomainAvailability(parkedDomain);
                    setDomainAvailability(availability);

                    if (availability.available && availability.pricing) {
                        setDomainPrice(availability.pricing);
                        setShowRegistrationForm(true);
                        setDomainParkingStatus({ message: `Domain ${parkedDomain} is available for registration!`, type: 'success' });
                    } else if (availability.available && !availability.pricing) {
                        setDomainParkingStatus({ message: `Domain ${parkedDomain} is available but pricing information is not available. Cannot proceed with registration.`, type: 'error' });
                        setShowRegistrationForm(false);
                    } else if (availability.status === 'PRICE_TOO_HIGH') {
                        setDomainParkingStatus({ message: availability.error || `Domain price exceeds maximum allowed`, type: 'info' });
                        setShowRegistrationForm(false);
                    } else {
                        setDomainParkingStatus({ message: `Domain ${parkedDomain} is not available.`, type: 'info' });
                    }
                } catch (error) {
                    console.error('Error checking domain:', error);
                    setDomainParkingStatus({ message: `Error checking domain: ${error.message}`, type: 'error' });
                }
            } else if (result && result.availableForRegistration === false) {
                // Domain is not available for registration
                setDomainParkingStatus('');
                setDomainAvailability({
                    available: false,
                    domain: result.domain,
                    status: result.status
                });
            } else {
                setDomainParkingStatus({ message: 'Error: Failed to register domain', type: 'error' });
            }
        } catch (error) {
            console.error('Error registering domain:', error);
            setDomainParkingStatus({ message: `Error: ${error.message || 'Failed to register domain'}`, type: 'error' });
        }
    };

    const handleParkDomainStep1 = async () => {
        try {
            setDomainParkingStatus({ message: 'Initializing AI Cloud security...', type: 'info' });

            const result = await parkDomainStep1();

            if (result && result.certificateArn && result.dnsRecords) {
                setCertificateArn(result.certificateArn);
                setDnsRecords(result.dnsRecords);
                setParkingStep(2);
                setDomainParkingStatus({ message: 'Security protocols initialized. Verifying domain ownership...', type: 'info' });
            } else {
                setDomainParkingStatus({ message: 'Error: Failed to request certificate', type: 'error' });
            }
        } catch (error) {
            console.error('Error in step 1:', error);
            setDomainParkingStatus({ message: `Error: ${error.message || 'Failed to request certificate'}`, type: 'error' });
        }
    };

    const handleParkDomainStep2 = async () => {
        try {
            setDomainParkingStatus({ message: 'Configuring AI Cloud infrastructure...', type: 'info' });

            const result = await parkDomainStep2();

            if (result && result.success) {
                setParkingStep(3);
                setDomainParkingStatus({ message: 'Infrastructure configured. Validating security certificates...', type: 'info' });
                startCertificatePolling();
            } else {
                setDomainParkingStatus({ message: 'Error: Failed to configure infrastructure', type: 'error' });
            }
        } catch (error) {
            console.error('Error in step 2:', error);
            setDomainParkingStatus({ message: `Error: ${error.message || 'Failed to configure infrastructure'}`, type: 'error' });
        }
    };

    const startCertificatePolling = async () => {
        setIsPolling(true);

        let pollInterval;

        // Check immediately first
        const checkStatus = async () => {
            try {
                const status = await parkDomainStatus();

                if (status.status === 'ISSUED') {
                    if (pollInterval) clearInterval(pollInterval);
                    setIsPolling(false);
                    handleParkDomainStep3();
                    return true; // Stop polling
                } else if (status.status === 'FAILED') {
                    if (pollInterval) clearInterval(pollInterval);
                    setIsPolling(false);
                    setDomainParkingStatus({ message: 'Certificate validation failed', type: 'error' });
                    return true; // Stop polling
                }
                return false; // Continue polling
            } catch (error) {
                console.error('Polling error:', error);
                if (pollInterval) clearInterval(pollInterval);
                setIsPolling(false);
                setDomainParkingStatus({ message: 'Error checking certificate status', type: 'error' });
                return true; // Stop polling on error
            }
        };

        // Check immediately
        const shouldStop = await checkStatus();
        if (shouldStop) return;

        // Then continue polling every 10 seconds
        pollInterval = setInterval(checkStatus, 10000);

        // Stop polling after 10 minutes
        setTimeout(() => {
            if (pollInterval) clearInterval(pollInterval);
            if (isPolling) {
                setIsPolling(false);
                setDomainParkingStatus({ message: 'Certificate validation timeout', type: 'error' });
            }
        }, 600000);
    };

    const handleParkDomainStep3 = async () => {
        try {
            setDomainParkingStatus({ message: 'Deploying AI Cloud console and applications...', type: 'info' });

            const result = await parkDomainStep3();

            if (result && result.appsDistributionId) {
                setDistributionId(result.appsDistributionId);
                setCloudfrontDomain(result.appsDistributionDomain);
                if (result.domain) setParkedDomain(result.domain);

                if (result.nextStep === 'parkDomainStep4') {
                    // Proceed directly to Step 4
                    setDomainParkingStatus({ message: 'Apps infrastructure ready. Deploying public portal...', type: 'info' });
                    setTimeout(() => handleParkDomainStep4(), 2000);
                } else {
                    setParkingStep(4);
                    setDomainParkingStatus({ message: 'Apps infrastructure created. Waiting for deployment...', type: 'info' });
                    startCloudFrontPolling();
                }
            } else {
                setDomainParkingStatus({ message: 'Error: Failed to deploy AI Cloud infrastructure', type: 'error' });
            }
        } catch (error) {
            console.error('Error in step 3:', error);
            setDomainParkingStatus({ message: `Error: ${error.message || 'Failed to create distribution'}`, type: 'error' });
        }
    };

    const handleParkDomainStep4 = async () => {
        try {
            setDomainParkingStatus({ message: 'Creating AI Cloud public portal...', type: 'info' });

            const result = await parkDomainStep4();

            if (result && result.filesDistributionId) {
                // Store files distribution info
                setDistributionId(result.filesDistributionId);
                setCloudfrontDomain(result.filesDistributionDomain);
                if (result.domain) setParkedDomain(result.domain);
                setParkingStep(5);
                setDomainParkingStatus({ message: 'Public portal created. Waiting for deployment...', type: 'info' });
                startCloudFrontPolling();
            } else {
                setDomainParkingStatus({ message: 'Error: Failed to create public portal', type: 'error' });
            }
        } catch (error) {
            console.error('Error in step 4:', error);
            setDomainParkingStatus({ message: `Error: ${error.message || 'Failed to create public portal'}`, type: 'error' });
        }
    };

    const startCloudFrontPolling = async () => {
        setIsPolling(true);

        let pollInterval;

        // Check immediately first
        const checkStatus = async () => {
            try {
                const status = await checkCloudFrontStatus();

                if (status.deployed) {
                    if (pollInterval) clearInterval(pollInterval);
                    setIsPolling(false);

                    if (status.nextStep === 'parkDomainStep4') {
                        // Apps distribution deployed, proceed to Step 4
                        setDomainParkingStatus({ message: 'Apps infrastructure deployed. Creating public portal...', type: 'info' });
                        setTimeout(() => handleParkDomainStep4(), 2000);
                    } else {
                        // Files distribution deployed, all complete
                        setDomainParkingStatus({ message: `AI Cloud fully deployed and ready! Your domain ${status.domain || parkedDomain} is now live!`, type: 'success' });
                        setParkingStep(6); // Final step
                    }
                    return true; // Stop polling
                } else {
                    setDomainParkingStatus({ message: `Deploying infrastructure... Status: ${status.status}`, type: 'info' });
                    return false; // Continue polling
                }
            } catch (error) {
                console.error('CloudFront polling error:', error);
                if (pollInterval) clearInterval(pollInterval);
                setIsPolling(false);
                setDomainParkingStatus({ message: 'Error checking deployment status', type: 'error' });
                return true; // Stop polling on error
            }
        };

        // Check immediately
        const shouldStop = await checkStatus();
        if (shouldStop) return;

        // Then continue polling every 30 seconds
        pollInterval = setInterval(checkStatus, 30000);

        // Stop polling after 20 minutes
        setTimeout(() => {
            if (pollInterval) clearInterval(pollInterval);
            if (isPolling) {
                setIsPolling(false);
                setDomainParkingStatus({ message: 'Deployment timeout - but domain should be ready soon!', type: 'info' });
            }
        }, 1200000);
    };

    const handleResetDomainParking = async () => {
        try {
            setDomainParkingStatus({ message: 'Resetting domain parking...', type: 'info' });
            const result = await resetDomainParking();

            if (result && result.success) {
                // Reset to step 1 (registered state)
                setParkingStep(1);
                setDnsRecords(null);
                setCertificateArn(null);
                setCloudfrontDomain(null);
                setDistributionId(null);
                setIsPolling(false);
                setDomainParkingStatus({ message: 'Domain parking reset successfully. You can restart the process.', type: 'success' });
            } else {
                setDomainParkingStatus({ message: 'Error: Failed to reset domain parking', type: 'error' });
            }
        } catch (error) {
            console.error('Error resetting domain parking:', error);
            setDomainParkingStatus({ message: `Error: ${error.message || 'Failed to reset domain parking'}`, type: 'error' });
        }
    };

    const loadAgencyPrivateData = async () => {
        try {
            const result = await getAgencyPrivateData();
            if (result?.success && result?.data) {
                setAgencyPrivateData({
                    STRIPE_SECRET_KEY: result.data.STRIPE_SECRET_KEY || '',
                    STRIPE_WEBHOOK_SECRET: result.data.STRIPE_WEBHOOK_SECRET || '',
                    STRIPE_SECRET_KEY_TEST: result.data.STRIPE_SECRET_KEY_TEST || '',
                    STRIPE_WEBHOOK_SECRET_TEST: result.data.STRIPE_WEBHOOK_SECRET_TEST || '',
                    stripeTestModeToken: result.data.stripeTestModeToken || ''
                });
            }
        } catch (error) {
            console.error('Error loading agency private data:', error);
        }
    };

    const handleSaveAgencyPrivateData = async () => {
        try {
            setSavingPrivateData(true);
            const result = await updateAgencyPrivateData(agencyPrivateData);
            if (result && result.success) {
                setSystemAlert({ msg: 'Stripe configuration saved successfully', type: 'success' });
            } else {
                setSystemAlert({ msg: 'Failed to save Stripe configuration', type: 'error' });
            }
        } catch (error) {
            console.error('Save private data error:', error);
            setSystemAlert({ msg: error.message || 'Failed to save Stripe configuration', type: 'error' });
        } finally {
            setSavingPrivateData(false);
        }
    };

    const handleSaveDomainPublicSettings = async () => {
        try {
            setSavingSettings(true);
            const result = await updateDomainPublicSettings(domainPublicSettings);
            if (result && result.success) {
                setSystemAlert({ msg: 'Domain settings saved successfully', type: 'success' });
            } else {
                setSystemAlert({ msg: 'Failed to save domain settings', type: 'error' });
            }
        } catch (error) {
            console.error('Save domain settings error:', error);
            setSystemAlert({ msg: error.message || 'Failed to save domain settings', type: 'error' });
        } finally {
            setSavingSettings(false);
        }
    };

    const handleLogoIconUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setSystemAlert({ msg: 'Please select an image file', type: 'error' });
            return;
        }

        try {
            setUploadingLogoIcon(true);
            setLogoIconUploadProgress(0);

            // Create a file with the specific name
            const modifiedFile = new File([file], 'logo_icon.png', { type: file.type });

            // Upload using the working uploadFileAPI
            const response = await uploadFileAPI(modifiedFile, 'files', (percentage) => {
                setLogoIconUploadProgress(percentage);
            });

            if (response) {
                setLogoIconExists(true);
                // Force refresh the preview
                checkLogosExist(parkedDomain);
                setSystemAlert({ msg: 'Logo icon uploaded successfully', type: 'success' });
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Logo icon upload error:', error);
            setSystemAlert({ msg: 'Failed to upload logo icon', type: 'error' });
        } finally {
            setUploadingLogoIcon(false);
            setLogoIconUploadProgress(0);
            event.target.value = null; // Reset input
        }
    };

    const handleFaviconUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setSystemAlert({ msg: 'Please select an image file', type: 'error' });
            return;
        }

        try {
            setUploadingFavicon(true);
            setFaviconUploadProgress(0);

            // Create a file with the specific name
            const modifiedFile = new File([file], 'favicon.ico', { type: file.type });

            // Upload using the working uploadFileAPI
            const response = await uploadFileAPI(modifiedFile, 'files', (percentage) => {
                setFaviconUploadProgress(percentage);
            });

            if (response) {
                setFaviconExists(true);
                // Force refresh the preview
                checkLogosExist(parkedDomain);
                setSystemAlert({ msg: 'Favicon uploaded successfully', type: 'success' });
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Favicon upload error:', error);
            setSystemAlert({ msg: 'Failed to upload favicon', type: 'error' });
        } finally {
            setUploadingFavicon(false);
            setFaviconUploadProgress(0);
            event.target.value = null; // Reset input
        }
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setSystemAlert({ msg: 'Please select an image file', type: 'error' });
            return;
        }

        try {
            setUploadingLogo(true);
            setLogoUploadProgress(0);

            // Create a file with the specific name
            const modifiedFile = new File([file], 'logo.png', { type: file.type });

            // Upload using the working uploadFileAPI
            const response = await uploadFileAPI(modifiedFile, 'files', (percentage) => {
                setLogoUploadProgress(percentage);
            });

            if (response) {
                setLogoExists(true);
                // Force refresh the preview
                checkLogosExist(parkedDomain);
                setSystemAlert({ msg: 'Logo uploaded successfully', type: 'success' });
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Logo upload error:', error);
            setSystemAlert({ msg: 'Failed to upload logo', type: 'error' });
        } finally {
            setUploadingLogo(false);
            setLogoUploadProgress(0);
            event.target.value = null; // Reset input
        }
    };

    const handleRemoveDomainParking = async () => {
        try {
            setDomainParkingStatus({ message: 'Removing domain parking completely...', type: 'info' });
            const result = await removeDomainParking();

            if (result && result.success) {
                // Reset everything to initial state
                setParkingStep(0);
                setParkedDomain('');
                setDnsRecords(null);
                setCertificateArn(null);
                setCloudfrontDomain(null);
                setDistributionId(null);
                setIsPolling(false);
                setDomainParkingStatus({ message: 'Domain parking removed completely.', type: 'success' });
            } else {
                setDomainParkingStatus({ message: 'Error: Failed to remove domain parking', type: 'error' });
            }
        } catch (error) {
            console.error('Remove domain parking error:', error);
            setDomainParkingStatus({ message: `Error removing domain parking: ${error.message}`, type: 'error' });
        }
    };

    return (
        <>
            {/* Platform Setup Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => goHome(navigate)}
                fullScreen
                PaperProps={{
                    sx: {
                        backgroundColor: isInIframe() ? '#fff' : '#f5f5f5'
                    }
                }}
            >
                <DialogTitle sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#fff', borderBottom: isInIframe() ? 'none' : '1px solid #e0e0e0', py: isInIframe() ? 1 : 1.5 }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: { xs: 1.5, sm: 0 }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Domain />
                            {!parkedDomain && editingName ? (
                                <TextField
                                    value={projectName}
                                    onChange={(e) => setProjectName(e.target.value)}
                                    onBlur={handleSaveProjectName}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveProjectName(); }}
                                    autoFocus
                                    size="small"
                                    variant="standard"
                                    sx={{ '& input': { fontSize: '1.25rem', fontWeight: 500 } }}
                                />
                            ) : (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                        ...(!parkedDomain && { cursor: 'pointer', '&:hover': { opacity: 0.7 } })
                                    }}
                                    onClick={() => !parkedDomain && setEditingName(true)}
                                >
                                    {parkedDomain || projectName || 'Platform Setup'}
                                </Typography>
                            )}
                            {parkingStep === 6 && (
                                <Chip
                                    icon={<CheckCircle sx={{ fontSize: 14 }} />}
                                    label="Live"
                                    size="small"
                                    color="success"
                                    onClick={() => setExpandedCloudSetup(!expandedCloudSetup)}
                                    sx={{ cursor: 'pointer', height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }}
                                />
                            )}
                        </Box>
                        {!initialLoading && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                justifyContent: { xs: 'space-between', sm: 'flex-end' }
                            }}>
                                {(parkingStep >= 6 || (parkedDomain && domainParkingStatus &&
                                    ((typeof domainParkingStatus === 'string' ? domainParkingStatus : domainParkingStatus?.message || '').includes('fully deployed')))) && (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={activeSettingsTab === 2 ? handleSaveAgencyPrivateData : handleSaveDomainPublicSettings}
                                        disabled={activeSettingsTab === 2 ? savingPrivateData : savingSettings}
                                        startIcon={<Save sx={{ color: 'white' }} />}
                                        size="small"
                                        sx={{ flex: { xs: 1, sm: 'none' }, ...(isInIframe() ? { minWidth: 160 } : {}) }}
                                    >
                                        {(activeSettingsTab === 2 ? savingPrivateData : savingSettings) ? 'Saving...' : 'Save'}
                                    </Button>
                                )}
                                {!isInIframe() && <Button
                                    onClick={() => goHome(navigate)}
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ArrowBack />}
                                    sx={{ flex: { xs: 1, sm: 'none' } }}
                                >
                                    Back
                                </Button>}
                            </Box>
                        )}
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ px: { xs: 1.5, sm: 3 } }}>
                    {initialLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                    <Box sx={{ width: '100%' }}>
                        {/* Domain Type Selection - Show first */}
                        {parkingStep === 0 && !showRegistrationForm && !domainType && !externalDomainNameservers && (
                            <Box sx={{ mb: 3, mt: 1 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Choose Domain Setup Type
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Card
                                            sx={{
                                                cursor: 'pointer',
                                                border: '2px solid transparent',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                            onClick={() => setDomainType('new')}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <ShoppingCart color="primary" sx={{ mr: 1 }} />
                                                    <Typography variant="h6">Register New Domain</Typography>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    Register a new domain through OpenKBS with automatic setup
                                                </Typography>
                                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                                    • Domains from €9/year<br/>
                                                    • Automatic configuration<br/>
                                                    • Privacy protection included
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Card
                                            sx={{
                                                cursor: 'pointer',
                                                border: '2px solid transparent',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                            onClick={() => setDomainType('external')}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Language color="primary" sx={{ mr: 1 }} />
                                                    <Typography variant="h6">Use Existing Domain</Typography>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    Connect a domain you already own from another registrar
                                                </Typography>
                                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                                    • Keep your current registrar<br/>
                                                    • Point NS records to us<br/>
                                                    • Full control remains yours
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Domain Input - Show after type selection */}
                        {parkingStep === 0 && !showRegistrationForm && domainType && !externalDomainNameservers && (
                            <Box sx={{ mb: 3, mt: 1 }}>
                                {/* Back button */}
                                <Button
                                    variant="text"
                                    startIcon={<ExpandLess />}
                                    onClick={() => setDomainType(null)}
                                    sx={{ mb: 2 }}
                                >
                                    Back to domain type selection
                                </Button>

                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {domainType === 'new' ? 'Register New Domain' : 'Connect Existing Domain'}
                                </Typography>

                                <TextField
                                    label="Your Domain"
                                    placeholder="yourdomain.com"
                                    value={parkedDomain}
                                    onChange={(e) => setParkedDomain(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    helperText={domainType === 'new'
                                        ? "Enter the domain you want to register"
                                        : "Enter your existing domain name"}
                                    sx={{ mb: 2 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={handleRegisterDomain}
                                        disabled={!parkedDomain.trim() || checkingAvailability}
                                        size="large"
                                        fullWidth
                                    >
                                        {domainType === 'new' ? 'Check & Register' : 'Create Hosted Zone'}
                                    </Button>
                                    {domainType === 'new' && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<Search />}
                                            onClick={handleCheckAvailability}
                                            disabled={!parkedDomain.trim() || checkingAvailability}
                                            size="large"
                                        >
                                            {checkingAvailability ? 'Checking...' : 'Check'}
                                        </Button>
                                    )}
                                </Box>

                                {/* Domain Availability Status */}
                                {domainAvailability && (
                                    <Alert
                                        severity={domainAvailability.available ? 'success' : 'warning'}
                                        sx={{ mt: 2 }}
                                    >
                                        {domainAvailability.available ? (
                                            <>
                                                <Typography variant="body2">
                                                    <strong>{domainAvailability.domain}</strong> is available for registration!
                                                </Typography>
                                                {domainAvailability.pricing && (
                                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                                        Registration: €{domainAvailability.pricing.registrationPrice}/year ({domainAvailability.pricing.registrationPrice * 100} credits)
                                                        {domainAvailability.pricing.renewalPrice && (
                                                            <>, Renewal: €{domainAvailability.pricing.renewalPrice}/year ({domainAvailability.pricing.renewalPrice * 100} credits)</>
                                                        )}
                                                    </Typography>
                                                )}
                                            </>
                                        ) : (
                                            <Typography variant="body2">
                                                {domainAvailability.domain} is not available ({domainAvailability.status})
                                            </Typography>
                                        )}
                                    </Alert>
                                )}
                            </Box>
                        )}

                        {/* NS Records Display for External Domains */}
                        {parkingStep === 0 && externalDomainNameservers && (
                            <Box sx={{ mb: 3, mt: 1 }}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        Update Your NS Records
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Please update the nameserver (NS) records at your domain registrar to point to these AWS Route53 nameservers:
                                    </Typography>
                                </Alert>

                                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                        Nameservers to add at your registrar:
                                    </Typography>
                                    {externalDomainNameservers.map((ns, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Dns color="primary" sx={{ mr: 1 }} />
                                            <TextField
                                                value={ns}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                InputProps={{
                                                    readOnly: true,
                                                    sx: { fontFamily: 'monospace' }
                                                }}
                                            />
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{ ml: 1 }}
                                                onClick={() => {
                                                    navigator.clipboard.writeText(ns);
                                                    setSystemAlert({
                                                        type: 'success',
                                                        message: `Copied: ${ns}`
                                                    });
                                                }}
                                            >
                                                Copy
                                            </Button>
                                        </Box>
                                    ))}
                                </Paper>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    Instructions for Popular Registrars:
                                </Typography>

                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><Language /></ListItemIcon>
                                        <ListItemText
                                            primary="GoDaddy"
                                            secondary="My Products → Domain Settings → Nameservers → Change → Custom → Enter nameservers"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><Language /></ListItemIcon>
                                        <ListItemText
                                            primary="Namecheap"
                                            secondary="Domain List → Manage → Nameservers → Custom DNS → Add nameservers"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><Language /></ListItemIcon>
                                        <ListItemText
                                            primary="Cloudflare"
                                            secondary="Domain Registration → Configure → Nameservers → Change"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><Language /></ListItemIcon>
                                        <ListItemText
                                            primary="Google Domains"
                                            secondary="DNS → Custom name servers → Add all 4 nameservers"
                                        />
                                    </ListItem>
                                </List>

                                <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Important:</strong> NS propagation can take 15 minutes to 48 hours. We'll check automatically every 30 seconds.
                                    </Typography>
                                </Alert>

                                {/* Verification Status */}
                                {isPolling && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2">
                                            Checking NS records... (Attempt {nsVerificationAttempts})
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={async () => {
                                            const result = await verifyExternalDomainNS();
                                            if (result && result.verified) {
                                                setDomainParkingStatus({
                                                    message: 'NS records verified! Proceeding...',
                                                    type: 'success'
                                                });
                                                setParkingStep(1);
                                                setExternalDomainNameservers(null);
                                            } else {
                                                setDomainParkingStatus({
                                                    message: result?.message || 'NS records not yet propagated',
                                                    type: 'info'
                                                });
                                            }
                                        }}
                                        disabled={isPolling}
                                    >
                                        Verify NS Records Now
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setExternalDomainNameservers(null);
                                            setDomainType(null);
                                            setParkedDomain('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* Domain Registration Form */}
                        {parkingStep === 0 && showRegistrationForm && (
                            <Box sx={{ mb: 3 }}>
                                {/* Available domain message */}
                                {domainAvailability && domainAvailability.available && (
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            <strong>Domain {parkedDomain} is available for registration!</strong>
                                        </Typography>
                                    </Alert>
                                )}

                                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ShoppingCart color="primary" />
                                        <Typography variant="h6">
                                            Register {parkedDomain}
                                        </Typography>
                                    </Box>
                                    {domainPrice && (
                                        <Chip
                                            label={`€${domainPrice.registrationPrice || '0'}/year (${(domainPrice.registrationPrice || 0) * 100} credits)`}
                                            color="primary"
                                            variant="outlined"
                                            size="medium"
                                        />
                                    )}
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                {/* Info block with pricing */}
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        • Privacy protection will be enabled for your contact information<br/>
                                        • Domain will be set to auto-renew (can be changed later)<br/>
                                        • Registration typically takes 15-30 minutes<br/>
                                        {domainPrice && (
                                            <><strong>• Total cost: €{domainPrice.registrationPrice} ({domainPrice.registrationPrice * 100} credits)</strong></>
                                        )}
                                    </Typography>
                                </Alert>

                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Contact Information
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="First Name"
                                            value={domainContactInfo.firstName}
                                            onChange={(e) => setDomainContactInfo({
                                                ...domainContactInfo,
                                                firstName: e.target.value
                                            })}
                                            required
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Last Name"
                                            value={domainContactInfo.lastName}
                                            onChange={(e) => setDomainContactInfo({
                                                ...domainContactInfo,
                                                lastName: e.target.value
                                            })}
                                            required
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Email"
                                            type="email"
                                            value={domainContactInfo.email}
                                            onChange={(e) => setDomainContactInfo({
                                                ...domainContactInfo,
                                                email: e.target.value
                                            })}
                                            required
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Phone Number"
                                            value={domainContactInfo.phoneNumber}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                // Only allow +, digits, and dot
                                                if (value === '' || value.match(/^\+?[0-9.]*$/)) {
                                                    setDomainContactInfo({
                                                        ...domainContactInfo,
                                                        phoneNumber: value
                                                    });
                                                }
                                            }}
                                            placeholder="+1.2125551234"
                                            helperText="e.g., +1.2125551234"
                                            error={domainContactInfo.phoneNumber && !domainContactInfo.phoneNumber.match(/^\+\d{1,4}\.\d{6,}$/)}
                                            required
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>Contact Type</InputLabel>
                                            <Select
                                                value={domainContactInfo.contactType}
                                                onChange={(e) => setDomainContactInfo({
                                                    ...domainContactInfo,
                                                    contactType: e.target.value
                                                })}
                                                label="Contact Type"
                                            >
                                                <MenuItem value="PERSON">Individual</MenuItem>
                                                <MenuItem value="COMPANY">Company</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {domainContactInfo.contactType === 'COMPANY' && (
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Organization Name"
                                                value={domainContactInfo.organizationName}
                                                onChange={(e) => setDomainContactInfo({
                                                    ...domainContactInfo,
                                                    organizationName: e.target.value
                                                })}
                                                required
                                                fullWidth
                                                helperText="Required for company registrations"
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                                            Address Information
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            label="Address Line 1"
                                            value={domainContactInfo.addressLine1}
                                            onChange={(e) => setDomainContactInfo({
                                                ...domainContactInfo,
                                                addressLine1: e.target.value
                                            })}
                                            required
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            label="Address Line 2 (Optional)"
                                            value={domainContactInfo.addressLine2}
                                            onChange={(e) => setDomainContactInfo({
                                                ...domainContactInfo,
                                                addressLine2: e.target.value
                                            })}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="City"
                                            value={domainContactInfo.city}
                                            onChange={(e) => setDomainContactInfo({
                                                ...domainContactInfo,
                                                city: e.target.value
                                            })}
                                            required
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        {domainContactInfo.countryCode === 'US' ? (
                                            <FormControl fullWidth required>
                                                <InputLabel>State</InputLabel>
                                                <Select
                                                    value={domainContactInfo.state}
                                                    onChange={(e) => setDomainContactInfo({
                                                        ...domainContactInfo,
                                                        state: e.target.value
                                                    })}
                                                    label="State"
                                                >
                                                    <MenuItem value="AL">Alabama</MenuItem>
                                                    <MenuItem value="AK">Alaska</MenuItem>
                                                    <MenuItem value="AZ">Arizona</MenuItem>
                                                    <MenuItem value="AR">Arkansas</MenuItem>
                                                    <MenuItem value="CA">California</MenuItem>
                                                    <MenuItem value="CO">Colorado</MenuItem>
                                                    <MenuItem value="CT">Connecticut</MenuItem>
                                                    <MenuItem value="DE">Delaware</MenuItem>
                                                    <MenuItem value="DC">District of Columbia</MenuItem>
                                                    <MenuItem value="FL">Florida</MenuItem>
                                                    <MenuItem value="GA">Georgia</MenuItem>
                                                    <MenuItem value="HI">Hawaii</MenuItem>
                                                    <MenuItem value="ID">Idaho</MenuItem>
                                                    <MenuItem value="IL">Illinois</MenuItem>
                                                    <MenuItem value="IN">Indiana</MenuItem>
                                                    <MenuItem value="IA">Iowa</MenuItem>
                                                    <MenuItem value="KS">Kansas</MenuItem>
                                                    <MenuItem value="KY">Kentucky</MenuItem>
                                                    <MenuItem value="LA">Louisiana</MenuItem>
                                                    <MenuItem value="ME">Maine</MenuItem>
                                                    <MenuItem value="MD">Maryland</MenuItem>
                                                    <MenuItem value="MA">Massachusetts</MenuItem>
                                                    <MenuItem value="MI">Michigan</MenuItem>
                                                    <MenuItem value="MN">Minnesota</MenuItem>
                                                    <MenuItem value="MS">Mississippi</MenuItem>
                                                    <MenuItem value="MO">Missouri</MenuItem>
                                                    <MenuItem value="MT">Montana</MenuItem>
                                                    <MenuItem value="NE">Nebraska</MenuItem>
                                                    <MenuItem value="NV">Nevada</MenuItem>
                                                    <MenuItem value="NH">New Hampshire</MenuItem>
                                                    <MenuItem value="NJ">New Jersey</MenuItem>
                                                    <MenuItem value="NM">New Mexico</MenuItem>
                                                    <MenuItem value="NY">New York</MenuItem>
                                                    <MenuItem value="NC">North Carolina</MenuItem>
                                                    <MenuItem value="ND">North Dakota</MenuItem>
                                                    <MenuItem value="OH">Ohio</MenuItem>
                                                    <MenuItem value="OK">Oklahoma</MenuItem>
                                                    <MenuItem value="OR">Oregon</MenuItem>
                                                    <MenuItem value="PA">Pennsylvania</MenuItem>
                                                    <MenuItem value="RI">Rhode Island</MenuItem>
                                                    <MenuItem value="SC">South Carolina</MenuItem>
                                                    <MenuItem value="SD">South Dakota</MenuItem>
                                                    <MenuItem value="TN">Tennessee</MenuItem>
                                                    <MenuItem value="TX">Texas</MenuItem>
                                                    <MenuItem value="UT">Utah</MenuItem>
                                                    <MenuItem value="VT">Vermont</MenuItem>
                                                    <MenuItem value="VA">Virginia</MenuItem>
                                                    <MenuItem value="WA">Washington</MenuItem>
                                                    <MenuItem value="WV">West Virginia</MenuItem>
                                                    <MenuItem value="WI">Wisconsin</MenuItem>
                                                    <MenuItem value="WY">Wyoming</MenuItem>
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <TextField
                                                label="State/Province"
                                                value={domainContactInfo.state}
                                                onChange={(e) => setDomainContactInfo({
                                                    ...domainContactInfo,
                                                    state: e.target.value
                                                })}
                                                fullWidth
                                            />
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            label="ZIP/Postal Code"
                                            value={domainContactInfo.zipCode}
                                            onChange={(e) => setDomainContactInfo({
                                                ...domainContactInfo,
                                                zipCode: e.target.value
                                            })}
                                            placeholder={domainContactInfo.countryCode === 'US' ? "12345" : ""}
                                            helperText={domainContactInfo.countryCode === 'US' ? "5 or 9 digits" : ""}
                                            required
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Country</InputLabel>
                                            <Select
                                                value={domainContactInfo.countryCode}
                                                onChange={(e) => setDomainContactInfo({
                                                    ...domainContactInfo,
                                                    countryCode: e.target.value,
                                                    state: '' // Reset state when country changes
                                                })}
                                                label="Country"
                                            >
                                                {availableCountries.map(country => (
                                                    <MenuItem key={country.code} value={country.code}>
                                                        {country.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>


                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                onClick={handleRegisterNewDomain}
                                                disabled={registeringDomain ||
                                                    !domainContactInfo.firstName ||
                                                    !domainContactInfo.lastName ||
                                                    !domainContactInfo.email ||
                                                    !domainContactInfo.phoneNumber ||
                                                    !domainContactInfo.addressLine1 ||
                                                    !domainContactInfo.city ||
                                                    (domainContactInfo.countryCode === 'US' && !domainContactInfo.state) ||
                                                    !domainContactInfo.zipCode ||
                                                    (domainContactInfo.contactType === 'COMPANY' && !domainContactInfo.organizationName)
                                                }
                                                fullWidth
                                            >
                                                {registeringDomain ? 'Registering Domain...' : 'Register Domain'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    setShowRegistrationForm(false);
                                                    setDomainAvailability(null);
                                                }}
                                                disabled={registeringDomain}
                                            >
                                                Cancel
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Domain Registration Pending */}
                        {parkingStep === 0 && pollingRegistrationStatus && (
                            <Box sx={{ mb: 3, textAlign: 'center', py: 4 }}>
                                <Timer sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Domain Registration in Progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    {parkedDomain} is being registered with AWS Route53 Domains
                                </Typography>
                                <CircularProgress sx={{ mt: 3 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    This process typically takes 15-30 minutes
                                </Typography>
                                <Alert severity="info" sx={{ mt: 3 }}>
                                    You can close this dialog and come back later. We'll continue checking the registration status.
                                </Alert>
                            </Box>
                        )}

                        {/* Stepper - Show when process has started */}
                        {parkingStep > 0 && (
                            <Box>
                                {/* Show stepper only if not completed or if expanded (via Live chip in header) */}
                                <Collapse in={parkingStep < 6 || expandedCloudSetup}>
                                    <Stepper activeStep={parkingStep === 6 ? 3 : Math.min(parkingStep - 1, 4)} orientation="vertical">
                                {/* Step 1: Domain Registration */}
                                <Step>
                                    <StepLabel
                                        icon={<Domain />}
                                        StepIconProps={{
                                            style: { color: parkingStep >= 1 ? '#4caf50' : undefined }
                                        }}
                                    >
                                        Domain Registration
                                    </StepLabel>
                                    <StepContent>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Domain <Chip label={parkedDomain} size="small" /> registered successfully
                                            </Typography>
                                            {parkingStep === 1 && (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<Security sx={{ color: 'white' }} />}
                                                    onClick={handleParkDomainStep1}
                                                    sx={{ mt: 2 }}
                                                >
                                                    Request SSL Certificate
                                                </Button>
                                            )}
                                        </Box>
                                    </StepContent>
                                </Step>

                                {/* Step 2: SSL Certificate */}
                                <Step>
                                    <StepLabel
                                        icon={<Security />}
                                        StepIconProps={{
                                            style: { color: parkingStep >= 2 ? '#4caf50' : undefined }
                                        }}
                                    >
                                        SSL Certificate Setup
                                    </StepLabel>
                                    <StepContent>
                                        <Box sx={{ mb: 2 }}>
                                            {parkingStep === 2 && dnsRecords && (
                                                <>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                        SSL certificate requested. Add these DNS records to validate your domain:
                                                    </Typography>
                                                    <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                                                        {dnsRecords.map((record, index) => (
                                                            <Box key={index} sx={{ mb: index < dnsRecords.length - 1 ? 2 : 0 }}>
                                                                <Typography variant="subtitle2" color="primary">Record {index + 1}:</Typography>
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                                    <strong>Name:</strong> {record.Name}<br/>
                                                                    <strong>Type:</strong> {record.Type}<br/>
                                                                    <strong>Value:</strong> {record.Value}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Paper>
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<Dns sx={{ color: 'white' }} />}
                                                        onClick={handleParkDomainStep2}
                                                    >
                                                        Add DNS Records
                                                    </Button>
                                                </>
                                            )}
                                            {parkingStep > 2 && (
                                                <Typography variant="body2" color="success.main">
                                                    SSL certificate validation in progress
                                                </Typography>
                                            )}
                                        </Box>
                                    </StepContent>
                                </Step>

                                {/* Step 3: Certificate Validation */}
                                <Step>
                                    <StepLabel
                                        icon={parkingStep === 3 ? <CircularProgress size={20} /> : <Dns />}
                                        StepIconProps={{
                                            style: { color: parkingStep >= 3 ? '#4caf50' : undefined }
                                        }}
                                    >
                                        Certificate Validation
                                    </StepLabel>
                                    <StepContent>
                                        <Box sx={{ mb: 2 }}>
                                            {parkingStep === 3 && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <CircularProgress size={20} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Validating SSL certificate... This may take 5-30 minutes.
                                                    </Typography>
                                                </Box>
                                            )}
                                            {parkingStep > 3 && (
                                                <Typography variant="body2" color="success.main">
                                                    SSL certificate validated successfully
                                                </Typography>
                                            )}
                                        </Box>
                                    </StepContent>
                                </Step>

                                {/* Step 4: CloudFront Deployment */}
                                <Step>
                                    <StepLabel
                                        icon={parkingStep === 4 && isPolling ? <CircularProgress size={20} /> : <CloudQueue />}
                                        StepIconProps={{
                                            style: { color: parkingStep >= 4 ? '#4caf50' : undefined }
                                        }}
                                    >
                                        Cloud Deployments
                                    </StepLabel>
                                    <StepContent>
                                        <Box sx={{ mb: 2 }}>
                                            {(parkingStep === 4 || parkingStep === 5) && isPolling && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <CircularProgress size={20} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Deploying CloudFront distribution... This may take 10-15 minutes.
                                                    </Typography>
                                                </Box>
                                            )}
                                            {parkingStep === 6 && parkedDomain && (
                                                <Box>
                                                    <List dense sx={{ mt: 2 }}>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                        <WebAsset sx={{ fontSize: 16, mr: 1 }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                            Public Website
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                                secondary={
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                            cursor: 'pointer',
                                                                            '&:hover': { textDecoration: 'underline' }
                                                                        }}
                                                                        onClick={() => window.open(`https://${parkedDomain}`, '_blank')}
                                                                    >
                                                                        {parkedDomain}
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItem>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                        <CloudQueue sx={{ fontSize: 16, mr: 1 }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                            Web Console
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                                secondary={
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                            cursor: 'pointer',
                                                                            '&:hover': { textDecoration: 'underline' }
                                                                        }}
                                                                        onClick={() => window.open(`https://console.${parkedDomain}`, '_blank')}
                                                                    >
                                                                        console.{parkedDomain}
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItem>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                                                        onClick={() => setExpandedMicroservices(!expandedMicroservices)}>
                                                                        <Api sx={{ fontSize: 16, mr: 1 }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                            Microservices
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                                            ({servicesList?.length} services)
                                                                        </Typography>
                                                                        {expandedMicroservices ? <ExpandLess sx={{ ml: 1 }} /> : <ExpandMore sx={{ ml: 1 }} />}
                                                                    </Box>
                                                                }
                                                                secondary={
                                                                    <Collapse in={expandedMicroservices}>
                                                                        <Box sx={{ mt: 1 }}>
                                                                            {servicesList.map((service, index) => (
                                                                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', minWidth: 180 }}>
                                                                                        {parkedDomain.split('.')[0]}.{service.domain}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', ml: 1 }}>
                                                                                        {service.desc}
                                                                                    </Typography>
                                                                                </Box>
                                                                            ))}
                                                                        </Box>
                                                                    </Collapse>
                                                                }
                                                            />
                                                        </ListItem>
                                                    </List>
                                                </Box>
                                            )}
                                            {(parkingStep >= 4 && parkingStep < 6) && parkedDomain && (
                                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Public Website: <Chip
                                                            label={parkedDomain}
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => window.open(`https://${parkedDomain}`, '_blank')}
                                                            sx={{ cursor: 'pointer' }}
                                                            color="success"
                                                        />
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Management Console: <Chip
                                                            label={`console.${parkedDomain}`}
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => window.open(`https://console.${parkedDomain}`, '_blank')}
                                                            sx={{ cursor: 'pointer' }}
                                                            color="primary"
                                                        />
                                                    </Typography>
                                                    {cloudfrontDomain && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            CloudFront URL: <Chip
                                                                label={cloudfrontDomain}
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => window.open(`https://${cloudfrontDomain}`, '_blank')}
                                                                sx={{ cursor: 'pointer' }}
                                                            />
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </StepContent>
                                </Step>
                                    </Stepper>
                                </Collapse>
                            </Box>
                        )}

                        {/* Domain Public Settings with Tabs */}
                        {(parkingStep >= 6 || (parkedDomain && domainParkingStatus &&
                            ((typeof domainParkingStatus === 'string' ? domainParkingStatus : domainParkingStatus?.message || '').includes('fully deployed')))) && (
                            <Card variant="outlined" sx={{ mt: 3, overflow: 'visible', borderColor: '#e0e0e0', boxShadow: 'none' }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    p: { xs: 2, sm: 3 },
                                    overflow: 'visible'
                                }}>
                                    {/* Sidebar Navigation */}
                                    <List
                                        sx={{
                                            width: { xs: '100%', md: 200 },
                                            flexShrink: 0,
                                            display: 'flex',
                                            flexDirection: { xs: 'row', md: 'column' },
                                            gap: 0.5,
                                            borderRight: { md: '1px solid' },
                                            borderBottom: { xs: '1px solid', md: 'none' },
                                            borderColor: 'divider',
                                            pb: { xs: 1, md: 0 },
                                            pr: { md: 2 },
                                            mr: { md: 3 },
                                            mb: { xs: 2, md: 0 },
                                            overflowX: { xs: 'auto', md: 'visible' },
                                            position: { md: 'sticky' },
                                            top: { md: 80 },
                                            alignSelf: { md: 'flex-start' },
                                            height: { md: 'fit-content' }
                                        }}
                                    >
                                        {[
                                            { label: 'General', icon: <Settings fontSize="small" /> },
                                            { label: 'Pricing', icon: <AttachMoney fontSize="small" /> },
                                            { label: 'Stripe', icon: <CreditCard fontSize="small" /> },
                                            { label: 'Users', icon: <HowToReg fontSize="small" /> },
                                            { label: 'Admins', icon: <AdminPanelSettings fontSize="small" /> },
                                            { label: 'Storage', icon: <Storage fontSize="small" /> },
                                            { label: 'Database', icon: <DataObject fontSize="small" /> },
                                            { label: 'Pulse', icon: <Wifi fontSize="small" /> },
                                            { label: 'Functions', icon: <Functions fontSize="small" /> }
                                        ].map((item, index) => (
                                            <ListItem key={item.label} disablePadding sx={{ width: { xs: 'auto', md: '100%' } }}>
                                                <ListItemButton
                                                    selected={activeSettingsTab === index}
                                                    onClick={() => setActiveSettingsTab(index)}
                                                    sx={{
                                                        borderRadius: { xs: 1, md: '0 4px 4px 0' },
                                                        minWidth: { xs: 'auto', md: '100%' },
                                                        px: { xs: 1.5, md: 2 },
                                                        py: 1,
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        gap: { xs: 0.5, sm: 1 },
                                                        '&.Mui-selected': {
                                                            backgroundColor: 'rgba(59, 134, 201, 0.08)',
                                                            borderLeft: { md: '3px solid #3B86C9' },
                                                            color: '#3B86C9',
                                                            '&:hover': { backgroundColor: 'rgba(59, 134, 201, 0.12)' }
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: { xs: 'auto', sm: 36 }, color: 'inherit' }}>
                                                        {item.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item.label}
                                                        primaryTypographyProps={{
                                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                                            fontWeight: activeSettingsTab === index ? 600 : 400
                                                        }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>

                                    {/* Content Area */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {/* Tab 0: General Settings */}
                                    {activeSettingsTab === 0 && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Configure public settings for your AI Agency domain
                                            </Typography>
                                            <Grid container spacing={3}>
                                                {/* Left Column - Basic Settings */}
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Typography variant="h6" gutterBottom>
                                                            General Settings
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <TextField
                                                                fullWidth
                                                                label="Company Name"
                                                                value={domainPublicSettings.COMPANY_NAME}
                                                                onChange={(e) => setDomainPublicSettings({
                                                                    ...domainPublicSettings,
                                                                    COMPANY_NAME: e.target.value
                                                                })}
                                                            />
                                                            <Tooltip title="Your company or organization name">
                                                                <Info sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <TextField
                                                                fullWidth
                                                                label="Google Client ID"
                                                                value={domainPublicSettings.GOOGLE_CLIENT_ID}
                                                                onChange={(e) => setDomainPublicSettings({
                                                                    ...domainPublicSettings,
                                                                    GOOGLE_CLIENT_ID: e.target.value
                                                                })}
                                                            />
                                                            <Tooltip title="Google OAuth Client ID for authentication">
                                                                <Info sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                            </Tooltip>
                                                        </Box>

                                                        {/* Advanced Settings Section */}
                                                        <Paper
                                                            elevation={0}
                                                            sx={{
                                                                mt: 2,
                                                                p: 2,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                backgroundColor: 'background.paper'
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    cursor: 'pointer',
                                                                    mb: expandedAdvancedSettings ? 2 : 0
                                                                }}
                                                                onClick={() => setExpandedAdvancedSettings(!expandedAdvancedSettings)}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Settings sx={{ mr: 1, color: 'text.secondary' }} />
                                                                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                                                                        Advanced Settings
                                                                    </Typography>
                                                                </Box>
                                                                <IconButton size="small">
                                                                    {expandedAdvancedSettings ? <ExpandLess /> : <ExpandMore />}
                                                                </IconButton>
                                                            </Box>

                                                            <Collapse in={expandedAdvancedSettings}>
                                                                <Divider sx={{ mb: 2 }} />
                                                                <Box sx={{ pl: 4 }}>
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        mb: 2
                                                                    }}>
                                                                        <Box>
                                                                            <Typography variant="body1">
                                                                                Show Transaction History
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Display transaction history and account details in the billing section
                                                                            </Typography>
                                                                        </Box>
                                                                        <Switch
                                                                            checked={domainPublicSettings.ADVANCED_SETTINGS?.TRANSACTIONS !== false}
                                                                            onChange={(e) => setDomainPublicSettings({
                                                                                ...domainPublicSettings,
                                                                                ADVANCED_SETTINGS: {
                                                                                    ...domainPublicSettings.ADVANCED_SETTINGS,
                                                                                    TRANSACTIONS: e.target.checked
                                                                                }
                                                                            })}
                                                                            color="primary"
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                            </Collapse>
                                                        </Paper>
                                                    </Box>
                                                </Grid>

                                                {/* Right Column - Brand Assets */}
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Typography variant="h6" gutterBottom>
                                                            Brand Assets
                                                        </Typography>

                                                        {/* Favicon Card */}
                                                        <Card variant="outlined" sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Box sx={{
                                                                    width: 64,
                                                                    height: 64,
                                                                    border: '2px solid #e0e0e0',
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    backgroundColor: '#fafafa',
                                                                    flexShrink: 0
                                                                }}>
                                                                    {parkedDomain && faviconExists ? (
                                                                        <img
                                                                            src={`https://${parkedDomain}/favicon.ico?t=${Date.now()}`}
                                                                            alt="Favicon"
                                                                            style={{
                                                                                maxWidth: '32px',
                                                                                maxHeight: '32px',
                                                                                objectFit: 'contain'
                                                                            }}
                                                                            onLoad={() => setFaviconExists(true)}
                                                                            onError={() => setFaviconExists(false)}
                                                                        />
                                                                    ) : (
                                                                        <ImageIcon sx={{ fontSize: 32, color: '#bdbdbd' }} />
                                                                    )}
                                                                </Box>
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="subtitle2" gutterBottom>
                                                                        Favicon
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Browser tab icon (32x32px)
                                                                    </Typography>
                                                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                                                        <Button
                                                                            variant="contained"
                                                                            color="secondary"
                                                                            component="label"
                                                                            size="small"
                                                                            disabled={uploadingFavicon}
                                                                            startIcon={uploadingFavicon ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CloudUpload sx={{ color: 'white' }} />}
                                                                            sx={{ width: 'auto' }}
                                                                        >
                                                                            {uploadingFavicon ? `${faviconUploadProgress}%` : 'Upload'}
                                                                            <input
                                                                                type="file"
                                                                                hidden
                                                                                accept="image/*,.ico"
                                                                                onChange={handleFaviconUpload}
                                                                            />
                                                                        </Button>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Card>

                                                        {/* Logo Icon Card */}
                                                        <Card variant="outlined" sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Box sx={{
                                                                    width: 64,
                                                                    height: 64,
                                                                    border: '2px solid #e0e0e0',
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    backgroundColor: '#fafafa',
                                                                    flexShrink: 0
                                                                }}>
                                                                    {parkedDomain && logoIconExists ? (
                                                                        <img
                                                                            src={`https://${parkedDomain}/logo_icon.png?t=${Date.now()}`}
                                                                            alt="Logo Icon"
                                                                            style={{
                                                                                maxWidth: '56px',
                                                                                maxHeight: '56px',
                                                                                objectFit: 'contain'
                                                                            }}
                                                                            onLoad={() => setLogoIconExists(true)}
                                                                            onError={() => setLogoIconExists(false)}
                                                                        />
                                                                    ) : (
                                                                        <ImageIcon sx={{ fontSize: 32, color: '#bdbdbd' }} />
                                                                    )}
                                                                </Box>
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="subtitle2" gutterBottom>
                                                                        Logo Icon
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Square logo (512x512px)
                                                                    </Typography>
                                                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                                                        <Button
                                                                            variant="contained"
                                                                            color="secondary"
                                                                            component="label"
                                                                            size="small"
                                                                            disabled={uploadingLogoIcon}
                                                                            startIcon={uploadingLogoIcon ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CloudUpload sx={{ color: 'white' }} />}
                                                                            sx={{ width: 'auto' }}
                                                                        >
                                                                            {uploadingLogoIcon ? `${logoIconUploadProgress}%` : 'Upload'}
                                                                            <input
                                                                                type="file"
                                                                                hidden
                                                                                accept="image/*"
                                                                                onChange={handleLogoIconUpload}
                                                                            />
                                                                        </Button>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Card>

                                                        {/* Horizontal Logo Card */}
                                                        <Card variant="outlined" sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Box sx={{
                                                                    width: 120,
                                                                    height: 64,
                                                                    border: '2px solid #e0e0e0',
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    backgroundColor: '#fafafa',
                                                                    flexShrink: 0
                                                                }}>
                                                                    {parkedDomain && logoExists ? (
                                                                        <img
                                                                            src={`https://${parkedDomain}/logo.png?t=${Date.now()}`}
                                                                            alt="Logo"
                                                                            style={{
                                                                                maxWidth: '110px',
                                                                                maxHeight: '56px',
                                                                                objectFit: 'contain'
                                                                            }}
                                                                            onLoad={() => setLogoExists(true)}
                                                                            onError={() => setLogoExists(false)}
                                                                        />
                                                                    ) : (
                                                                        <ImageIcon sx={{ fontSize: 32, color: '#bdbdbd' }} />
                                                                    )}
                                                                </Box>
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="subtitle2" gutterBottom>
                                                                        Full Logo
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Logo with text (2:1 ratio)
                                                                    </Typography>
                                                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                                                        <Button
                                                                            variant="contained"
                                                                            color="secondary"
                                                                            component="label"
                                                                            size="small"
                                                                            disabled={uploadingLogo}
                                                                            startIcon={uploadingLogo ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CloudUpload sx={{ color: 'white' }} />}
                                                                            sx={{ width: 'auto' }}
                                                                        >
                                                                            {uploadingLogo ? `${logoUploadProgress}%` : 'Upload'}
                                                                            <input
                                                                                type="file"
                                                                                hidden
                                                                                accept="image/*"
                                                                                onChange={handleLogoUpload}
                                                                            />
                                                                        </Button>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Card>
                                                    </Box>
                                                </Grid>
                                            </Grid>

                                            {/* Welcome Email Template Editor */}
                                            <Box sx={{ mt: 4 }}>
                                                <Typography variant="h6" gutterBottom>
                                                    Welcome Email Template
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    Customize the welcome email sent to new users. Leave fields empty to use default templates.
                                                </Typography>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => {
                                                            // Load default template based on company name
                                                            const companyName = domainPublicSettings.COMPANY_NAME || 'Your Company';
                                                            setDomainPublicSettings({
                                                                ...domainPublicSettings,
                                                                WELCOME_EMAIL_TEMPLATE: {
                                                                    subject: `Welcome to ${companyName}`,
                                                                    htmlBody: `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Welcome to {companyName}</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background-color: #f8f9fa; padding: 20px; text-align: center; border-radius: 5px; }
        .content { padding: 20px 0; }
        .footer { text-align: center; padding-top: 20px; color: #666; font-size: 14px; }
        a { color: #007bff; text-decoration: none; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Welcome to {companyName}</h1>
        </div>
        <div class="content">
            <p>Hi there,</p>
            <p>Thank you for joining <strong>{companyName}</strong>! We're excited to have you as part of our community.</p>
            <p>You can get started by visiting our platform:</p>
            <p style="text-align: center;">
                <a href="https://{domain}" style="background-color: #007bff; color: white; padding: 10px 20px; border-radius: 5px; display: inline-block;">Visit {companyName}</a>
            </p>
            <p>If you have any questions, feel free to reach out to our support team.</p>
            <p>Best regards,<br>The {companyName} Team</p>
        </div>
        <div class="footer">
            <p>© ${new Date().getFullYear()} {companyName}. All rights reserved.</p>
        </div>
    </div>
</body>
</html>`,
                                                                    textBody: `Welcome to {companyName}

Hi there,

Thank you for joining {companyName}! We're excited to have you as part of our community.

You can get started by visiting our platform at: https://{domain}

If you have any questions, feel free to reach out to our support team.

Best regards,
The {companyName} Team

© ${new Date().getFullYear()} {companyName}. All rights reserved.`
                                                                }
                                                            });
                                                        }}
                                                    >
                                                        Load Default Template
                                                    </Button>
                                                </Box>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            fullWidth
                                                            label="Email Subject"
                                                            value={domainPublicSettings.WELCOME_EMAIL_TEMPLATE?.subject || ''}
                                                            onChange={(e) => setDomainPublicSettings({
                                                                ...domainPublicSettings,
                                                                WELCOME_EMAIL_TEMPLATE: {
                                                                    ...domainPublicSettings.WELCOME_EMAIL_TEMPLATE,
                                                                    subject: e.target.value
                                                                }
                                                            })}
                                                            placeholder={`Welcome to ${domainPublicSettings.COMPANY_NAME || 'Your Platform'}`}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            rows={10}
                                                            label="HTML Email Body"
                                                            value={domainPublicSettings.WELCOME_EMAIL_TEMPLATE?.htmlBody || ''}
                                                            onChange={(e) => setDomainPublicSettings({
                                                                ...domainPublicSettings,
                                                                WELCOME_EMAIL_TEMPLATE: {
                                                                    ...domainPublicSettings.WELCOME_EMAIL_TEMPLATE,
                                                                    htmlBody: e.target.value
                                                                }
                                                            })}
                                                            placeholder="HTML template for the email body. Use {companyName} and {domain} as placeholders."
                                                            helperText="You can use HTML tags and the following variables: {companyName}, {domain}"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            rows={6}
                                                            label="Plain Text Email Body"
                                                            value={domainPublicSettings.WELCOME_EMAIL_TEMPLATE?.textBody || ''}
                                                            onChange={(e) => setDomainPublicSettings({
                                                                ...domainPublicSettings,
                                                                WELCOME_EMAIL_TEMPLATE: {
                                                                    ...domainPublicSettings.WELCOME_EMAIL_TEMPLATE,
                                                                    textBody: e.target.value
                                                                }
                                                            })}
                                                            placeholder="Plain text version of the email for clients that don't support HTML"
                                                            helperText="Plain text fallback. Use {companyName} and {domain} as placeholders."
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>

                                            {/* Legal Documents Editor */}
                                            <LegalDocumentsEditor
                                                domainPublicSettings={domainPublicSettings}
                                                setDomainPublicSettings={setDomainPublicSettings}
                                            />
                                        </Box>
                                    )}

                                    {/* Tab 1: Subscription Plans */}
                                    {activeSettingsTab === 1 && (
                                        <Box>
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                justifyContent: { xs: 'flex-start', sm: 'space-between' },
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                gap: { xs: 2, sm: 2 },
                                                mb: 2
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    {shouldResetSubscriptions && (
                                                        <Button
                                                            variant="outlined"
                                                            color="warning"
                                                            onClick={() => {
                                                                setDomainPublicSettings(prev => ({
                                                                    ...prev,
                                                                    SUBSCRIPTION_PLANS: DEFAULT_SUBSCRIPTION_PLANS
                                                                }));
                                                                setSystemAlert({ msg: 'Subscription plans reset to defaults', type: 'info' });
                                                            }}
                                                        >
                                                            Reset to Defaults
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                            <SubscriptionPlansManager
                                                plans={domainPublicSettings.SUBSCRIPTION_PLANS}
                                                onChange={(updatedPlans) => setDomainPublicSettings({
                                                    ...domainPublicSettings,
                                                    SUBSCRIPTION_PLANS: updatedPlans
                                                })}
                                                creditPricing={domainPublicSettings.CREDIT_PRICING}
                                                onCreditPricingChange={(updatedPricing) => setDomainPublicSettings({
                                                    ...domainPublicSettings,
                                                    CREDIT_PRICING: updatedPricing
                                                })}
                                                registrationBonus={domainPublicSettings.REGISTRATION_BONUS}
                                                onRegistrationBonusChange={(updatedBonus) => setDomainPublicSettings({
                                                    ...domainPublicSettings,
                                                    REGISTRATION_BONUS: updatedBonus
                                                })}
                                                onNavigateToStripeTab={() => setActiveSettingsTab(2)}
                                            />

                                        </Box>
                                    )}

                                    {/* Tab 2: Stripe Integration */}
                                    {activeSettingsTab === 2 && (
                                        <Box>
                                            {/* Webhook Setup Instructions - AT THE TOP */}
                                            <Alert severity="info" sx={{ mb: 3 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    Webhook Setup Instructions:
                                                </Typography>
                                                <Typography variant="body2" component="div">
                                                    <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                                        <li>Go to your Stripe Dashboard → Developers → Webhooks</li>
                                                        <li>Click "Add endpoint" and configure:</li>
                                                        <li style={{ marginLeft: '20px' }}>
                                                            <strong>Production URL:</strong>
                                                            <code style={{
                                                                backgroundColor: '#f5f5f5',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                display: 'block',
                                                                margin: '4px 0'
                                                            }}>
                                                                https://openkbs.stripe-event.vpc1.us/?key={KBData?.key?.substring(0,12)}&domain={parkedDomain}
                                                            </code>
                                                        </li>
                                                        <li style={{ marginLeft: '20px' }}>
                                                            <strong>Test URL:</strong>
                                                            <code style={{
                                                                backgroundColor: '#f5f5f5',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                display: 'block',
                                                                margin: '4px 0'
                                                            }}>
                                                                https://openkbs.stripe-event.vpc1.us/?key={KBData?.key?.substring(0,12)}&domain={parkedDomain}&test=1
                                                            </code>
                                                        </li>
                                                        <li>Select these events to listen for:
                                                            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                                                <li><code>charge.refunded</code></li>
                                                                <li><code>checkout.session.completed</code></li>
                                                                <li><code>customer.subscription.deleted</code></li>
                                                                <li><code>invoice.payment_succeeded</code></li>
                                                            </ul>
                                                        </li>
                                                        <li>Copy the signing secret (starts with whsec_) and paste it below</li>
                                                    </ol>
                                                </Typography>
                                            </Alert>

                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Configure Stripe payment integration for your platform
                                            </Typography>

                                            <Grid container spacing={3}>
                                                {/* Production Settings */}
                                                <Grid item xs={12}>
                                                    <Typography variant="h6" gutterBottom>
                                                        Production Environment
                                                    </Typography>
                                                    <Divider sx={{ mb: 2 }} />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Stripe Secret Key"
                                                            type={showStripeField.secretKey ? 'text' : 'password'}
                                                            value={agencyPrivateData.STRIPE_SECRET_KEY}
                                                            onChange={(e) => setAgencyPrivateData({
                                                                ...agencyPrivateData,
                                                                STRIPE_SECRET_KEY: e.target.value
                                                            })}
                                                            placeholder="sk_live_..."
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton size="small" onClick={() => setShowStripeField(s => ({ ...s, secretKey: !s.secretKey }))}>
                                                                            {showStripeField.secretKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                        <Tooltip title="Your Stripe secret key for production payments">
                                                            <Info sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                        </Tooltip>
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Webhook Secret"
                                                            type={showStripeField.webhookSecret ? 'text' : 'password'}
                                                            value={agencyPrivateData.STRIPE_WEBHOOK_SECRET}
                                                            onChange={(e) => setAgencyPrivateData({
                                                                ...agencyPrivateData,
                                                                STRIPE_WEBHOOK_SECRET: e.target.value
                                                            })}
                                                            placeholder="whsec_..."
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton size="small" onClick={() => setShowStripeField(s => ({ ...s, webhookSecret: !s.webhookSecret }))}>
                                                                            {showStripeField.webhookSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                        <Tooltip title="Webhook endpoint secret for verifying Stripe events">
                                                            <Info sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                        </Tooltip>
                                                    </Box>
                                                </Grid>

                                                {/* Test Environment */}
                                                <Grid item xs={12}>
                                                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                                        Test Environment
                                                    </Typography>
                                                    <Divider sx={{ mb: 2 }} />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Test Secret Key"
                                                            type={showStripeField.testSecretKey ? 'text' : 'password'}
                                                            value={agencyPrivateData.STRIPE_SECRET_KEY_TEST}
                                                            onChange={(e) => setAgencyPrivateData({
                                                                ...agencyPrivateData,
                                                                STRIPE_SECRET_KEY_TEST: e.target.value
                                                            })}
                                                            placeholder="sk_test_..."
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton size="small" onClick={() => setShowStripeField(s => ({ ...s, testSecretKey: !s.testSecretKey }))}>
                                                                            {showStripeField.testSecretKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                        <Tooltip title="Your Stripe secret key for test payments">
                                                            <Info sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                        </Tooltip>
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Test Webhook Secret"
                                                            type={showStripeField.testWebhookSecret ? 'text' : 'password'}
                                                            value={agencyPrivateData.STRIPE_WEBHOOK_SECRET_TEST}
                                                            onChange={(e) => setAgencyPrivateData({
                                                                ...agencyPrivateData,
                                                                STRIPE_WEBHOOK_SECRET_TEST: e.target.value
                                                            })}
                                                            placeholder="whsec_..."
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton size="small" onClick={() => setShowStripeField(s => ({ ...s, testWebhookSecret: !s.testWebhookSecret }))}>
                                                                            {showStripeField.testWebhookSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                        <Tooltip title="Test webhook endpoint secret for verifying Stripe events">
                                                            <Info sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                        </Tooltip>
                                                    </Box>
                                                </Grid>

                                                {/* Test Mode Token */}
                                                <Grid item xs={12}>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                                        Test Mode Access Token
                                                    </Typography>
                                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                                        <Typography variant="body2">
                                                            This secret token enables test mode for Stripe payments. When users append
                                                            <strong> ?{agencyPrivateData.stripeTestModeToken || 'your_token'}=1 </strong>
                                                            to the subscription or credit purchase page URL, they will use Stripe's test environment.
                                                            This allows testing subscriptions and payments with test credit cards without real money transactions.
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                                            Keep this token secret! Only share with trusted users who need to test payments.
                                                        </Typography>
                                                    </Alert>
                                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'flex-start' } }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Test Mode Token"
                                                            value={agencyPrivateData.stripeTestModeToken || ''}
                                                            onChange={(e) => setAgencyPrivateData({
                                                                ...agencyPrivateData,
                                                                stripeTestModeToken: e.target.value
                                                            })}
                                                            placeholder="e.g., test_a1b2c3d4e5f6g7h8"
                                                            helperText="Secret token to enable test mode. Users must append this as URL parameter"
                                                        />
                                                        <Button
                                                            startIcon={<RefreshOutlined />}
                                                            variant="outlined"
                                                            onClick={() => {
                                                                const newToken = generateRandomToken();
                                                                setAgencyPrivateData({
                                                                    ...agencyPrivateData,
                                                                    stripeTestModeToken: newToken
                                                                });
                                                            }}
                                                            sx={{ minWidth: 140, mt: { sm: 1 } }}
                                                        >
                                                            Generate
                                                        </Button>
                                                    </Box>
                                                </Grid>

                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Tab 3: My Users */}
                                    {activeSettingsTab === 3 && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                View and manage users registered to your whitelabel domain
                                            </Typography>

                                            {loadingSubusers ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : domainSubusers.length === 0 ? (
                                                <Alert severity="info">
                                                    No users found for this domain.
                                                </Alert>
                                            ) : (
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                                        Total Users: {domainSubusers.length}
                                                    </Typography>
                                                    <List sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                    }}>
                                                        {domainSubusers.map((user, index) => (
                                                            <React.Fragment key={user.email}>
                                                                <ListItem
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        '&:hover': {
                                                                            backgroundColor: 'action.hover'
                                                                        }
                                                                    }}
                                                                >
                                                                    <ListItemText
                                                                        primary={user.email}
                                                                        sx={{ flex: 1 }}
                                                                    />
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                                                                        <Typography variant="caption" color={user.billingActive !== false ? 'success.main' : 'text.disabled'} sx={{ mr: 0.5 }}>
                                                                            {user.billingActive !== false ? 'Active' : 'Inactive'}
                                                                        </Typography>
                                                                        <Switch
                                                                            size="small"
                                                                            checked={user.billingActive !== false}
                                                                            onChange={async (e) => {
                                                                                const newValue = e.target.checked;
                                                                                try {
                                                                                    await setSubuserBillingActive(user.email, newValue);
                                                                                    setDomainSubusers(prev => prev.map(u =>
                                                                                        u.email === user.email ? { ...u, billingActive: newValue } : u
                                                                                    ));
                                                                                } catch (error) {
                                                                                    setSystemAlert({ type: 'error', message: error.message || 'Failed to update billing status' });
                                                                                }
                                                                            }}
                                                                        />
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            onClick={() => {
                                                                                const loginUrl = `https://console.${parkedDomain}/dashboard?ip=${encodeURIComponent(user.email)}&withLogout=1&whitelabelKbId=${kbId}`;
                                                                                window.open(loginUrl, '_blank');
                                                                            }}
                                                                            sx={{ ml: 1 }}
                                                                        >
                                                                            Login
                                                                        </Button>
                                                                    </Box>
                                                                </ListItem>
                                                                {index < domainSubusers.length - 1 && <Divider />}
                                                            </React.Fragment>
                                                        ))}
                                                    </List>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Tab 4: Admins */}
                                    {activeSettingsTab === 4 && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Manage admin access to this project. Admins can view and modify settings.
                                            </Typography>

                                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    placeholder="Enter email address"
                                                    value={adminEmail}
                                                    onChange={(e) => setAdminEmail(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleAddAdmin()}
                                                />
                                                <Button
                                                    variant="contained"
                                                    onClick={handleAddAdmin}
                                                    disabled={!adminEmail.trim()}
                                                    sx={{ whiteSpace: 'nowrap' }}
                                                >
                                                    Add Admin
                                                </Button>
                                            </Box>

                                            {collaboratorsError && (
                                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCollaboratorsError('')}>
                                                    {collaboratorsError}
                                                </Alert>
                                            )}

                                            {collaboratorsLoading ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : kbShares.length === 0 ? (
                                                <Alert severity="info">No admins added yet.</Alert>
                                            ) : (
                                                <List>
                                                    {kbShares.map((email) => (
                                                        <ListItem
                                                            key={email}
                                                            secondaryAction={
                                                                <IconButton edge="end" onClick={() => handleRemoveAdmin(email)}>
                                                                    <Delete />
                                                                </IconButton>
                                                            }
                                                        >
                                                            <ListItemText primary={email} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            )}
                                        </Box>
                                    )}

                                    {/* Tab 5: Storage */}
                                    {activeSettingsTab === 5 && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Manage S3 storage bucket for your application
                                            </Typography>

                                            {storageLoading ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : (
                                                <Box>
                                                    {/* Status Card */}
                                                    <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Storage color={elasticStorage?.enabled ? 'success' : 'disabled'} />
                                                                <Typography variant="h6">
                                                                    Storage Status
                                                                </Typography>
                                                            </Box>
                                                            <Chip
                                                                label={elasticStorage?.enabled ? 'Enabled' : 'Disabled'}
                                                                color={elasticStorage?.enabled ? 'success' : 'default'}
                                                                size="small"
                                                            />
                                                        </Box>
                                                        {elasticStorage?.enabled && (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Bucket:</strong> {elasticStorage.bucket || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Region:</strong> {elasticStorage.region || 'N/A'}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        <strong>Public Access:</strong>
                                                                    </Typography>
                                                                    <Switch
                                                                        checked={elasticStorage.public || false}
                                                                        onChange={async (e) => {
                                                                            const newValue = e.target.checked;
                                                                            // Update UI immediately
                                                                            setElasticStorage(prev => ({ ...prev, public: newValue }));
                                                                            try {
                                                                                await setElasticStoragePublic(newValue);
                                                                                setSystemAlert({ type: 'success', message: `Storage is now ${newValue ? 'public' : 'private'}` });
                                                                            } catch (error) {
                                                                                // Revert on error
                                                                                setElasticStorage(prev => ({ ...prev, public: !newValue }));
                                                                                setSystemAlert({ type: 'error', message: error.message || 'Failed to update storage access' });
                                                                            }
                                                                        }}
                                                                        size="small"
                                                                    />
                                                                </Box>
                                                                {/* SPA Fallback Configuration */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        <strong>SPA Fallback:</strong>
                                                                    </Typography>
                                                                    {!spaEditing ? (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Typography variant="body2" color={elasticStorage.spaFallback ? 'text.primary' : 'text.disabled'}>
                                                                                {elasticStorage.spaFallback || 'Not configured'}
                                                                            </Typography>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    setSpaPath(elasticStorage.spaFallback || '/index.html');
                                                                                    setSpaEditing(true);
                                                                                }}
                                                                            >
                                                                                <Edit fontSize="small" />
                                                                            </IconButton>
                                                                        </Box>
                                                                    ) : (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <TextField
                                                                                size="small"
                                                                                value={spaPath}
                                                                                onChange={(e) => setSpaPath(e.target.value)}
                                                                                placeholder="/index.html"
                                                                                sx={{ width: 200 }}
                                                                                disabled={spaSaving}
                                                                            />
                                                                            <IconButton
                                                                                size="small"
                                                                                color="primary"
                                                                                disabled={spaSaving}
                                                                                onClick={async () => {
                                                                                    setSpaSaving(true);
                                                                                    try {
                                                                                        await setSiteSPA(spaPath || null);
                                                                                        setElasticStorage(prev => ({ ...prev, spaFallback: spaPath || null }));
                                                                                        setSpaEditing(false);
                                                                                        setSystemAlert({ type: 'success', message: spaPath ? `SPA fallback set to ${spaPath}` : 'SPA fallback disabled' });
                                                                                    } catch (error) {
                                                                                        setSystemAlert({ type: 'error', message: error.message || 'Failed to update SPA fallback' });
                                                                                    } finally {
                                                                                        setSpaSaving(false);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {spaSaving ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                disabled={spaSaving}
                                                                                onClick={() => setSpaEditing(false)}
                                                                            >
                                                                                <CloseIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Card>

                                                    {/* Action Buttons */}
                                                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                                        {!elasticStorage?.enabled ? (
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                startIcon={<Add sx={{ color: 'white' }} />}
                                                                onClick={async () => {
                                                                    setStorageLoading(true);
                                                                    try {
                                                                        const kbRegion = KBData?.region || 'us-east-1';
                                                                        const result = await enableElasticStorage(kbRegion);
                                                                        setElasticStorage(result);
                                                                        setSystemAlert({ type: 'success', message: `Storage enabled in ${kbRegion}` });
                                                                    } catch (error) {
                                                                        setSystemAlert({ type: 'error', message: error.message || 'Failed to enable storage' });
                                                                    } finally {
                                                                        setStorageLoading(false);
                                                                    }
                                                                }}
                                                            >
                                                                Enable Storage {KBData?.region ? `(${KBData.region})` : ''}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                startIcon={<Delete />}
                                                                onClick={async () => {
                                                                    if (window.confirm('Are you sure you want to delete storage? All files will be lost.')) {
                                                                        setStorageLoading(true);
                                                                        try {
                                                                            await deleteElasticStorage(true);
                                                                            setElasticStorage({ enabled: false });
                                                                            setStorageObjects([]);
                                                                            setSystemAlert({ type: 'success', message: 'Storage deleted successfully' });
                                                                        } catch (error) {
                                                                            setSystemAlert({ type: 'error', message: error.message || 'Failed to delete storage' });
                                                                        } finally {
                                                                            setStorageLoading(false);
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                Delete Storage
                                                            </Button>
                                                        )}
                                                    </Box>

                                                    {/* File Browser */}
                                                    {elasticStorage?.enabled && (
                                                        <Card variant="outlined" sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <FolderOpen /> Files
                                                                </Typography>
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<RefreshOutlined />}
                                                                    onClick={async () => {
                                                                        setStorageObjectsLoading(true);
                                                                        try {
                                                                            const objects = await listStorageObjects();
                                                                            setStorageObjects(objects?.objects || []);
                                                                        } catch (error) {
                                                                            setSystemAlert({ type: 'error', message: error.message || 'Failed to refresh files' });
                                                                        } finally {
                                                                            setStorageObjectsLoading(false);
                                                                        }
                                                                    }}
                                                                >
                                                                    Refresh
                                                                </Button>
                                                            </Box>
                                                            {storageObjectsLoading ? (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                                                    <CircularProgress size={24} />
                                                                </Box>
                                                            ) : storageObjects.length === 0 ? (
                                                                <Alert severity="info">No files in storage</Alert>
                                                            ) : (
                                                                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                                                                    {storageObjects.map((obj, index) => (
                                                                        <React.Fragment key={obj.Key}>
                                                                            <ListItem
                                                                                secondaryAction={
                                                                                    <IconButton
                                                                                        edge="end"
                                                                                        color="error"
                                                                                        disabled={deletingStorageObject === obj.Key}
                                                                                        onClick={async () => {
                                                                                            if (window.confirm(`Delete ${obj.Key}?`)) {
                                                                                                setDeletingStorageObject(obj.Key);
                                                                                                try {
                                                                                                    await deleteStorageObject(obj.Key);
                                                                                                    setStorageObjects(storageObjects.filter(o => o.Key !== obj.Key));
                                                                                                    setSystemAlert({ type: 'success', message: 'File deleted' });
                                                                                                } catch (error) {
                                                                                                    setSystemAlert({ type: 'error', message: error.message });
                                                                                                } finally {
                                                                                                    setDeletingStorageObject(null);
                                                                                                }
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        {deletingStorageObject === obj.Key ? <CircularProgress size={20} /> : <Delete />}
                                                                                    </IconButton>
                                                                                }
                                                                            >
                                                                                <ListItemIcon>
                                                                                    <InsertDriveFile />
                                                                                </ListItemIcon>
                                                                                <ListItemText
                                                                                    primary={obj.Key}
                                                                                    secondary={`${(obj.Size / 1024).toFixed(2)} KB • ${new Date(obj.LastModified).toLocaleString()}`}
                                                                                />
                                                                            </ListItem>
                                                                            {index < storageObjects.length - 1 && <Divider />}
                                                                        </React.Fragment>
                                                                    ))}
                                                                </List>
                                                            )}
                                                        </Card>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Tab 6: Database */}
                                    {activeSettingsTab === 6 && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Manage PostgreSQL database for your application
                                            </Typography>

                                            {postgresLoading ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : (
                                                <Box>
                                                    {/* Status Card */}
                                                    <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <DataObject color={elasticPostgres?.enabled ? 'success' : 'disabled'} />
                                                                <Typography variant="h6">
                                                                    Database Status
                                                                </Typography>
                                                            </Box>
                                                            <Chip
                                                                label={elasticPostgres?.enabled ? 'Enabled' : 'Disabled'}
                                                                color={elasticPostgres?.enabled ? 'success' : 'default'}
                                                                size="small"
                                                            />
                                                        </Box>
                                                        {elasticPostgres?.enabled && (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Host:</strong> {elasticPostgres.host || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Port:</strong> {elasticPostgres.port || '5432'}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Database:</strong> {elasticPostgres.database || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Region:</strong> {elasticPostgres.region || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Card>

                                                    {/* Connection String */}
                                                    {elasticPostgres?.enabled && (
                                                        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                                <Typography variant="subtitle1" fontWeight="bold">
                                                                    Connection String
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={async () => {
                                                                            if (!postgresConnection) {
                                                                                try {
                                                                                    const conn = await getElasticPostgresConnection();
                                                                                    setPostgresConnection(conn);
                                                                                } catch (error) {
                                                                                    setSystemAlert({ type: 'error', message: error.message });
                                                                                }
                                                                            }
                                                                            setShowConnectionString(!showConnectionString);
                                                                        }}
                                                                    >
                                                                        {showConnectionString ? <VisibilityOff /> : <Visibility />}
                                                                    </IconButton>
                                                                    {showConnectionString && postgresConnection && (
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(postgresConnection.connectionString);
                                                                                setSystemAlert({ type: 'success', message: 'Connection string copied' });
                                                                            }}
                                                                        >
                                                                            <ContentCopy />
                                                                        </IconButton>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                            <TextField
                                                                fullWidth
                                                                value={showConnectionString && postgresConnection ? postgresConnection.connectionString : '••••••••••••••••••••'}
                                                                InputProps={{ readOnly: true }}
                                                                size="small"
                                                                type={showConnectionString ? 'text' : 'password'}
                                                            />
                                                        </Card>
                                                    )}

                                                    {/* Action Buttons */}
                                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                                        {!elasticPostgres?.enabled ? (
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                startIcon={<Add sx={{ color: 'white' }} />}
                                                                onClick={async () => {
                                                                    setPostgresLoading(true);
                                                                    try {
                                                                        const kbRegion = KBData?.region || 'us-east-1';
                                                                        const result = await enableElasticPostgres(kbRegion);
                                                                        setElasticPostgres(result);
                                                                        setSystemAlert({ type: 'success', message: `Database enabled in ${kbRegion}` });
                                                                    } catch (error) {
                                                                        setSystemAlert({ type: 'error', message: error.message || 'Failed to enable database' });
                                                                    } finally {
                                                                        setPostgresLoading(false);
                                                                    }
                                                                }}
                                                            >
                                                                Enable Database {KBData?.region ? `(${KBData.region})` : ''}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                startIcon={<Delete />}
                                                                onClick={() => setDeletePostgresDialogOpen(true)}
                                                            >
                                                                Delete Database
                                                            </Button>
                                                        )}
                                                    </Box>

                                                    {/* Delete Confirmation Dialog */}
                                                    <Dialog
                                                        open={deletePostgresDialogOpen}
                                                        onClose={() => setDeletePostgresDialogOpen(false)}
                                                    >
                                                        <DialogTitle>Delete Database?</DialogTitle>
                                                        <DialogContent>
                                                            <Alert severity="warning" sx={{ mb: 2 }}>
                                                                This action cannot be undone. All data will be permanently deleted.
                                                            </Alert>
                                                            <Typography>
                                                                Are you sure you want to delete the PostgreSQL database?
                                                            </Typography>
                                                        </DialogContent>
                                                        <DialogActions>
                                                            <Button onClick={() => setDeletePostgresDialogOpen(false)}>
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                color="error"
                                                                variant="contained"
                                                                onClick={async () => {
                                                                    setDeletePostgresDialogOpen(false);
                                                                    setPostgresLoading(true);
                                                                    try {
                                                                        await deleteElasticPostgres();
                                                                        setElasticPostgres({ enabled: false });
                                                                        setPostgresConnection(null);
                                                                        setSystemAlert({ type: 'success', message: 'Database deleted successfully' });
                                                                    } catch (error) {
                                                                        setSystemAlert({ type: 'error', message: error.message || 'Failed to delete database' });
                                                                    } finally {
                                                                        setPostgresLoading(false);
                                                                    }
                                                                }}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </DialogActions>
                                                    </Dialog>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Tab 7: Pulse */}
                                    {activeSettingsTab === 7 && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Manage real-time WebSocket messaging for your application
                                            </Typography>

                                            {pulseLoading ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : (
                                                <Box>
                                                    {/* Status Card */}
                                                    <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Wifi color={elasticPulse?.enabled ? 'success' : 'disabled'} />
                                                                <Typography variant="h6">
                                                                    Pulse Status
                                                                </Typography>
                                                            </Box>
                                                            <Chip
                                                                label={elasticPulse?.enabled ? 'Enabled' : 'Disabled'}
                                                                color={elasticPulse?.enabled ? 'success' : 'default'}
                                                                size="small"
                                                            />
                                                        </Box>
                                                        {elasticPulse?.enabled && elasticPulse?.endpoint && (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>WebSocket Endpoint:</strong>
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <TextField
                                                                        fullWidth
                                                                        value={elasticPulse.endpoint}
                                                                        InputProps={{ readOnly: true }}
                                                                        size="small"
                                                                    />
                                                                    <IconButton
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(elasticPulse.endpoint);
                                                                            setSystemAlert({ type: 'success', message: 'Endpoint copied' });
                                                                        }}
                                                                    >
                                                                        <ContentCopy />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Card>

                                                    {/* Action Buttons */}
                                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                                        {!elasticPulse?.enabled ? (
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                startIcon={<Add sx={{ color: 'white' }} />}
                                                                onClick={async () => {
                                                                    setPulseLoading(true);
                                                                    try {
                                                                        const kbRegion = KBData?.region || 'us-east-1';
                                                                        const result = await enableElasticPulse(kbRegion);
                                                                        setElasticPulse(result);
                                                                        setSystemAlert({ type: 'success', message: `Pulse enabled in ${kbRegion}` });
                                                                    } catch (error) {
                                                                        setSystemAlert({ type: 'error', message: error.message || 'Failed to enable Pulse' });
                                                                    } finally {
                                                                        setPulseLoading(false);
                                                                    }
                                                                }}
                                                            >
                                                                Enable Pulse {KBData?.region ? `(${KBData.region})` : ''}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                startIcon={<Delete />}
                                                                onClick={async () => {
                                                                    if (window.confirm('Are you sure you want to disable Pulse?')) {
                                                                        setPulseLoading(true);
                                                                        try {
                                                                            await disableElasticPulse();
                                                                            setElasticPulse({ enabled: false });
                                                                            setSystemAlert({ type: 'success', message: 'Pulse disabled successfully' });
                                                                        } catch (error) {
                                                                            setSystemAlert({ type: 'error', message: error.message || 'Failed to disable Pulse' });
                                                                        } finally {
                                                                            setPulseLoading(false);
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                Disable Pulse
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Tab 8: Functions */}
                                    {activeSettingsTab === 8 && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Manage serverless Lambda functions for your application
                                            </Typography>

                                            {functionsLoading ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : elasticFunctions.length === 0 ? (
                                                <Alert severity="info">
                                                    No functions deployed yet. Functions are created automatically when you deploy code.
                                                </Alert>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {elasticFunctions.map((func) => (
                                                        <Card key={func.functionName} variant="outlined" sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                                                <Box>
                                                                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Functions fontSize="small" />
                                                                        {func.functionName}
                                                                    </Typography>
                                                                    {func.url && (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                                                                {func.url}
                                                                            </Typography>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    navigator.clipboard.writeText(func.url);
                                                                                    setSystemAlert({ type: 'success', message: 'URL copied' });
                                                                                }}
                                                                            >
                                                                                <ContentCopy fontSize="small" />
                                                                            </IconButton>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={async () => {
                                                                            if (selectedFunction === func.functionName) {
                                                                                setSelectedFunction(null);
                                                                                setFunctionLogs([]);
                                                                            } else {
                                                                                setSelectedFunction(func.functionName);
                                                                                setFunctionLogsLoading(true);
                                                                                try {
                                                                                    const logs = await getElasticFunctionLogs(func.functionName);
                                                                                    setFunctionLogs(logs?.events || []);
                                                                                } catch (error) {
                                                                                    setSystemAlert({ type: 'error', message: error.message || 'Failed to load logs' });
                                                                                } finally {
                                                                                    setFunctionLogsLoading(false);
                                                                                }
                                                                            }
                                                                        }}
                                                                    >
                                                                        {selectedFunction === func.functionName ? 'Hide Logs' : 'View Logs'}
                                                                    </Button>
                                                                    <IconButton
                                                                        color="error"
                                                                        onClick={() => {
                                                                            setFunctionToDelete(func.functionName);
                                                                            setDeleteFunctionDialogOpen(true);
                                                                        }}
                                                                    >
                                                                        <Delete />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Region:</strong> {func.region || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Memory:</strong> {func.memorySize || 128} MB
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Timeout:</strong> {func.timeout || 30}s
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    <strong>Runtime:</strong> {func.runtime || 'nodejs18.x'}
                                                                </Typography>
                                                            </Box>

                                                            {/* Logs Section */}
                                                            <Collapse in={selectedFunction === func.functionName}>
                                                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                                        Recent Logs
                                                                    </Typography>
                                                                    {functionLogsLoading ? (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                                            <CircularProgress size={24} />
                                                                        </Box>
                                                                    ) : functionLogs.length === 0 ? (
                                                                        <Alert severity="info" sx={{ py: 0.5 }}>No logs available</Alert>
                                                                    ) : (
                                                                        <Box
                                                                            sx={{
                                                                                maxHeight: 300,
                                                                                overflow: 'auto',
                                                                                backgroundColor: '#1e1e1e',
                                                                                borderRadius: 1,
                                                                                p: 1,
                                                                                fontFamily: 'monospace',
                                                                                fontSize: '0.75rem'
                                                                            }}
                                                                        >
                                                                            {functionLogs.map((log, index) => (
                                                                                <Box key={index} sx={{ color: '#d4d4d4', mb: 0.5 }}>
                                                                                    <span style={{ color: '#6a9955' }}>
                                                                                        {new Date(log.timestamp).toISOString()}
                                                                                    </span>
                                                                                    {' '}{log.message}
                                                                                </Box>
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Collapse>
                                                        </Card>
                                                    ))}
                                                </Box>
                                            )}

                                            {/* Delete Function Dialog */}
                                            <Dialog
                                                open={deleteFunctionDialogOpen}
                                                onClose={() => setDeleteFunctionDialogOpen(false)}
                                            >
                                                <DialogTitle>Delete Function?</DialogTitle>
                                                <DialogContent>
                                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                                        This action cannot be undone.
                                                    </Alert>
                                                    <Typography>
                                                        Are you sure you want to delete the function <strong>{functionToDelete}</strong>?
                                                    </Typography>
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button onClick={() => setDeleteFunctionDialogOpen(false)}>
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        color="error"
                                                        variant="contained"
                                                        onClick={async () => {
                                                            setDeleteFunctionDialogOpen(false);
                                                            setFunctionsLoading(true);
                                                            try {
                                                                await deleteElasticFunction(functionToDelete);
                                                                setElasticFunctions(elasticFunctions.filter(f => f.functionName !== functionToDelete));
                                                                setSystemAlert({ type: 'success', message: 'Function deleted successfully' });
                                                            } catch (error) {
                                                                setSystemAlert({ type: 'error', message: error.message || 'Failed to delete function' });
                                                            } finally {
                                                                setFunctionsLoading(false);
                                                                setFunctionToDelete(null);
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>
                                        </Box>
                                    )}

                                    </Box>
                                </Box>
                            </Card>
                        )}

                        {/* Status Messages */}
                        {domainParkingStatus && (() => {
                            // Support both string and object formats
                            const message = typeof domainParkingStatus === 'string'
                                ? domainParkingStatus
                                : domainParkingStatus.message;
                            const type = typeof domainParkingStatus === 'string'
                                ? getStatusType(domainParkingStatus)
                                : domainParkingStatus.type || 'info';

                            const colors = {
                                success: { bg: 'rgba(76, 175, 80, 0.1)', border: 'rgba(76, 175, 80, 0.3)', text: 'success.main' },
                                info: { bg: 'rgba(33, 150, 243, 0.1)', border: 'rgba(33, 150, 243, 0.3)', text: 'primary.main' },
                                error: { bg: 'rgba(244, 67, 54, 0.1)', border: 'rgba(244, 67, 54, 0.3)', text: 'error.main' }
                            };
                            const color = colors[type] || colors.info;

                            return (
                                <Box sx={{
                                    mt: 2,
                                    p: 2,
                                    backgroundColor: color.bg,
                                    borderRadius: 1,
                                    border: `1px solid ${color.border}`
                                }}>
                                    <Typography variant="body2" color={color.text}>
                                        {message}
                                    </Typography>
                                </Box>
                            );
                        })()}

                        {/* Reset/Remove Buttons - only show if URL has reset=1 */}
                        {parkingStep > 0 && getQueryParamValue('reset') && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="text"
                                        color="warning"
                                        size="small"
                                        onClick={handleResetDomainParking}
                                    >
                                        Reset and Restart
                                    </Button>
                                    <Button
                                        variant="text"
                                        color="error"
                                        size="small"
                                        onClick={handleRemoveDomainParking}
                                    >
                                        Remove Completely
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};